// js/custom-element/behavior/openable-element.js
var OpenableElement = class extends CustomHTMLElement {
  static get observedAttributes() {
    return ['open'];
  }
  constructor() {
    super();
    if (Shopify.designMode) {
      this.rootDelegate.on('shopify:section:select', (event) => filterShopifyEvent(event, this, () => (this.open = true)));
      this.rootDelegate.on('shopify:section:deselect', (event) => filterShopifyEvent(event, this, () => (this.open = false)));
    }
    if (this.hasAttribute('append-body')) {
      const existingNode = document.getElementById(this.id);
      this.removeAttribute('append-body');
      if (existingNode && existingNode !== this) {
        existingNode.replaceWith(this.cloneNode(true));
        this.remove();
      } else {
        document.body.appendChild(this);
      }
    }
  }
  connectedCallback() {
    this.delegate.on('click', '.openable__overlay', () => (this.open = false));
    this.delegate.on('click', '[data-action="close"]', (event) => {
      event.stopPropagation();
      this.open = false;
    });
  }
  get requiresLoading() {
    return this.hasAttribute('href');
  }
  get open() {
    return this.hasAttribute('open');
  }
  set open(value) {
    if (value) {
      (async () => {
        await this._load();
        this.clientWidth;
        this.setAttribute('open', '');
      })();
    } else {
      this.removeAttribute('open');
    }
  }
  get shouldTrapFocus() {
    return true;
  }
  get returnFocusOnDeactivate() {
    return !this.hasAttribute('return-focus') || this.getAttribute('return-focus') === 'true';
  }
  get focusTrap() {
    return (this._focusTrap =
      this._focusTrap ||
      createFocusTrap(this, {
        fallbackFocus: this,
        initialFocus: this.hasAttribute('initial-focus-selector') ? this.getAttribute('initial-focus-selector') : void 0,
        clickOutsideDeactivates: (event) => !(event.target.hasAttribute('aria-controls') && event.target.getAttribute('aria-controls') === this.id),
        allowOutsideClick: (event) => event.target.hasAttribute('aria-controls') && event.target.getAttribute('aria-controls') === this.id,
        returnFocusOnDeactivate: this.returnFocusOnDeactivate,
        onDeactivate: () => (this.open = false),
        preventScroll: true,
      }));
  }
  attributeChangedCallback(name, oldValue, newValue) {
    switch (name) {
      case 'open':
        if (oldValue === null && newValue === '') {
          if (this.shouldTrapFocus) {
            setTimeout(() => this.focusTrap.activate(), 150);
          }
          triggerEvent(this, 'openable-element:open');
        } else if (newValue === null) {
          if (this.shouldTrapFocus) {
            this.focusTrap.deactivate();
          }
          triggerEvent(this, 'openable-element:close');
        }
    }
  }
  async _load() {
    if (!this.requiresLoading) {
      return;
    }
    triggerNonBubblingEvent(this, 'openable-element:load:start');
    const response = await fetch(this.getAttribute('href'));
    const element = document.createElement('div');
    element.innerHTML = await response.text();
    this.innerHTML = element.querySelector(this.tagName.toLowerCase()).innerHTML;
    this.removeAttribute('href');
    triggerNonBubblingEvent(this, 'openable-element:load:end');
  }
};
window.customElements.define('openable-element', OpenableElement);

// js/custom-element/behavior/collapsible-content.js
var CollapsibleContent = class extends OpenableElement {
  constructor() {
    super();
    this.ignoreNextTransition = this.open;
    this.addEventListener('shopify:block:select', () => (this.open = true));
    this.addEventListener('shopify:block:deselect', () => (this.open = false));
  }
  get animateItems() {
    return this.hasAttribute('animate-items');
  }
  attributeChangedCallback(name) {
    if (this.ignoreNextTransition) {
      return (this.ignoreNextTransition = false);
    }
    switch (name) {
      case 'open':
        this.style.overflow = 'hidden';
        const keyframes = {
          height: ['0px', `${this.scrollHeight}px`],
          visibility: ['hidden', 'visible'],
        };
        if (this.animateItems) {
          keyframes['opacity'] = this.open ? [0, 0] : [0, 1];
        }
        this.animate(keyframes, {
          duration: 500,
          direction: this.open ? 'normal' : 'reverse',
          easing: 'cubic-bezier(0.75, 0, 0.175, 1)',
        }).onfinish = () => {
          this.style.overflow = this.open ? 'visible' : 'hidden';
        };
        if (this.animateItems && this.open) {
          this.animate(
            {
              opacity: [0, 1],
              transform: ['translateY(10px)', 'translateY(0)'],
            },
            {
              duration: 250,
              delay: 250,
              easing: 'cubic-bezier(0.75, 0, 0.175, 1)',
            }
          );
        }
        triggerEvent(this, this.open ? 'openable-element:open' : 'openable-element:close');
    }
  }
};
window.customElements.define('collapsible-content', CollapsibleContent);

// js/custom-element/behavior/confirm-button.js
var ConfirmButton = class extends HTMLButtonElement {
  connectedCallback() {
    this.addEventListener('click', (event) => {
      if (!window.confirm(this.getAttribute('data-message') || 'Are you sure you wish to do this?')) {
        event.preventDefault();
      }
    });
  }
};
window.customElements.define('confirm-button', ConfirmButton, { extends: 'button' });

// js/custom-element/behavior/toggle-button.js
var ToggleButton = class extends HTMLButtonElement {
  static get observedAttributes() {
    return ['aria-expanded', 'aria-busy'];
  }
  constructor() {
    super();
    if (this.hasAttribute('loader')) {
      this._prepareButton();
    }
    this.addEventListener('click', this._onButtonClick.bind(this));
    this.rootDelegate = new main_default(document.documentElement);
  }
  _onButtonClick() {
    this.isExpanded = !this.isExpanded;
  }
  connectedCallback() {
    document.addEventListener('openable-element:close', (event) => {
      if (this.controlledElement === event.target) {
        this.isExpanded = false;
        event.stopPropagation();
      }
    });
    document.addEventListener('openable-element:open', (event) => {
      if (this.controlledElement === event.target) {
        this.isExpanded = true;
        event.stopPropagation();
      }
    });
    this.rootDelegate.on(
      'openable-element:load:start',
      `#${this.getAttribute('aria-controls')}`,
      () => {
        if (this.classList.contains('button')) {
          this.setAttribute('aria-busy', 'true');
        } else if (this.offsetParent !== null) {
          triggerEvent(document.documentElement, 'theme:loading:start');
        }
      },
      true
    );
    this.rootDelegate.on(
      'openable-element:load:end',
      `#${this.getAttribute('aria-controls')}`,
      () => {
        if (this.classList.contains('button')) {
          this.removeAttribute('aria-busy');
        } else if (this.offsetParent !== null) {
          triggerEvent(document.documentElement, 'theme:loading:end');
        }
      },
      true
    );
  }
  disconnectedCallback() {
    this.rootDelegate.destroy();
  }
  get isExpanded() {
    return this.getAttribute('aria-expanded') === 'true';
  }
  set isExpanded(value) {
    this.setAttribute('aria-expanded', value ? 'true' : 'false');
  }
  get controlledElement() {
    return document.getElementById(this.getAttribute('aria-controls'));
  }
  attributeChangedCallback(name, oldValue, newValue) {
    switch (name) {
      case 'aria-expanded':
        if (oldValue === 'false' && newValue === 'true') {
          if (!!this.controlledElement) this.controlledElement.open = true;
        } else if (oldValue === 'true' && newValue === 'false') {
          if (!!this.controlledElement) this.controlledElement.open = false;
        }
        break;
      case 'aria-busy':
        if (this.hasAttribute('loader')) {
          if (newValue === 'true') {
            this._startTransition();
          } else {
            this._endTransition();
          }
        }
        break;
    }
  }
};
Object.assign(ToggleButton.prototype, LoaderButtonMixin);
window.customElements.define('toggle-button', ToggleButton, { extends: 'button' });

// js/custom-element/behavior/toggle-link.js
var ToggleLink = class extends HTMLAnchorElement {
  static get observedAttributes() {
    return ['aria-expanded'];
  }
  constructor() {
    super();
    this.addEventListener('click', (event) => {
      event.preventDefault();
      this.isExpanded = !this.isExpanded;
    });
    this.rootDelegate = new main_default(document.documentElement);
  }
  connectedCallback() {
    this.rootDelegate.on(
      'openable-element:close',
      `#${this.getAttribute('aria-controls')}`,
      (event) => {
        if (this.controlledElement === event.target) {
          this.isExpanded = false;
        }
      },
      true
    );
    this.rootDelegate.on(
      'openable-element:open',
      `#${this.getAttribute('aria-controls')}`,
      (event) => {
        if (this.controlledElement === event.target) {
          this.isExpanded = true;
        }
      },
      true
    );
  }
  disconnectedCallback() {
    this.rootDelegate.destroy();
  }
  get isExpanded() {
    return this.getAttribute('aria-expanded') === 'true';
  }
  set isExpanded(value) {
    this.setAttribute('aria-expanded', value ? 'true' : 'false');
  }
  get controlledElement() {
    return document.querySelector(`#${this.getAttribute('aria-controls')}`);
  }
  attributeChangedCallback(name, oldValue, newValue) {
    switch (name) {
      case 'aria-expanded':
        if (oldValue === 'false' && newValue === 'true') {
          this.controlledElement.open = true;
        } else if (oldValue === 'true' && newValue === 'false') {
          this.controlledElement.open = false;
        }
    }
  }
};
window.customElements.define('toggle-link', ToggleLink, { extends: 'a' });

// js/custom-element/behavior/page-dots.js
var PageDots = class extends CustomHTMLElement {
  connectedCallback() {
    this.buttons = Array.from(this.querySelectorAll('button'));
    this.delegate.on('click', 'button', (event, target) => {
      this._dispatchEvent(this.buttons.indexOf(target));
    });
    if (this.hasAttribute('animation-timer')) {
      this.delegate.on('animationend', (event) => {
        if (event.elapsedTime > 0) {
          this._dispatchEvent((this.selectedIndex + 1 + this.buttons.length) % this.buttons.length);
        }
      });
    }
  }
  get selectedIndex() {
    return this.buttons.findIndex((button) => button.getAttribute('aria-current') === 'true');
  }
  set selectedIndex(selectedIndex) {
    this.buttons.forEach((button, index) => button.setAttribute('aria-current', selectedIndex === index ? 'true' : 'false'));
    if (this.hasAttribute('align-selected')) {
      const selectedItem = this.buttons[selectedIndex],
        windowHalfWidth = window.innerWidth / 2,
        boundingRect = selectedItem.getBoundingClientRect(),
        scrollableElement = this._findFirstScrollableElement(this.parentElement);
      if (scrollableElement) {
        scrollableElement.scrollTo({
          behavior: 'smooth',
          left: scrollableElement.scrollLeft + (boundingRect.left - windowHalfWidth) + boundingRect.width / 2,
        });
      }
    }
  }
  _dispatchEvent(index) {
    if (index !== this.selectedIndex) {
      this.dispatchEvent(
        new CustomEvent('page-dots:changed', {
          bubbles: true,
          detail: {
            index,
          },
        })
      );
    }
  }
  _findFirstScrollableElement(item, currentDepth = 0) {
    if (item === null || currentDepth > 3) {
      return null;
    }
    return item.scrollWidth > item.clientWidth ? item : this._findFirstScrollableElement(item.parentElement, currentDepth + 1);
  }
};
window.customElements.define('page-dots', PageDots);

// js/custom-element/behavior/prev-next-buttons.js
var PrevNextButtons = class extends HTMLElement {
  connectedCallback() {
    this.prevButton = this.querySelector('button:first-of-type');
    this.nextButton = this.querySelector('button:last-of-type');
    this.prevButton.addEventListener('click', () => this.prevButton.dispatchEvent(new CustomEvent('prev-next:prev', { bubbles: true })));
    this.nextButton.addEventListener('click', () => this.nextButton.dispatchEvent(new CustomEvent('prev-next:next', { bubbles: true })));
  }
  set isPrevDisabled(value) {
    this.prevButton.disabled = value;
  }
  set isNextDisabled(value) {
    this.nextButton.disabled = value;
  }
};
var PrevButton = class extends HTMLButtonElement {
  connectedCallback() {
    this.addEventListener('click', () => this.dispatchEvent(new CustomEvent('prev-next:prev', { bubbles: true })));
  }
};
var NextButton = class extends HTMLButtonElement {
  connectedCallback() {
    this.addEventListener('click', () => this.dispatchEvent(new CustomEvent('prev-next:next', { bubbles: true })));
  }
};
window.customElements.define('prev-next-buttons', PrevNextButtons);
window.customElements.define('prev-button', PrevButton, { extends: 'button' });
window.customElements.define('next-button', NextButton, { extends: 'button' });

// js/helper/dimensions.js
function getStickyHeaderOffset() {
  const documentStyles = getComputedStyle(document.documentElement);
  return parseInt(documentStyles.getPropertyValue('--header-height') || 0) * parseInt(documentStyles.getPropertyValue('--enable-sticky-header') || 0) + parseInt(documentStyles.getPropertyValue('--announcement-bar-height') || 0) * parseInt(documentStyles.getPropertyValue('--enable-sticky-announcement-bar') || 0);
}

// js/helper/throttle.js
function throttle(callback, delay3 = 15) {
  let throttleTimeout = null,
    storedEvent = null;
  const throttledEventHandler = (event) => {
    storedEvent = event;
    const shouldHandleEvent = !throttleTimeout;
    if (shouldHandleEvent) {
      callback(storedEvent);
      storedEvent = null;
      throttleTimeout = setTimeout(() => {
        throttleTimeout = null;
        if (storedEvent) {
          throttledEventHandler(storedEvent);
        }
      }, delay3);
    }
  };
  return throttledEventHandler;
}

// js/custom-element/behavior/scroll-spy.js
var ScrollSpy = class extends HTMLElement {
  connectedCallback() {
    this._createSvg();
    this.elementsToObserve = Array.from(this.querySelectorAll('a')).map((linkElement) => document.querySelector(linkElement.getAttribute('href')));
    this.navListItems = Array.from(this.querySelectorAll('li'));
    this.navItems = this.navListItems
      .map((listItem) => {
        const anchor = listItem.firstElementChild,
          targetID = anchor && anchor.getAttribute('href').slice(1),
          target = document.getElementById(targetID);
        return { listItem, anchor, target };
      })
      .filter((item) => item.target);
    this.drawPath();
    window.addEventListener('scroll', throttle(this.markVisibleSection.bind(this), 25));
    window.addEventListener('orientationchange', () => {
      window.addEventListener(
        'resize',
        () => {
          this.drawPath();
          this.markVisibleSection();
        },
        { once: true }
      );
    });
    this.markVisibleSection();
  }
  /**
   * Dynamically create the SVG element that will be used to "spy" the scroll
   */
  _createSvg() {
    this.navPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    const svgElement = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svgElement.insertAdjacentElement('beforeend', this.navPath);
    this.insertAdjacentElement('beforeend', svgElement);
    this.lastPathStart = this.lastPathEnd = null;
  }
  drawPath() {
    let path = [],
      pathIndent;
    this.navItems.forEach((item, i) => {
      const x = item.anchor.offsetLeft - 5,
        y = item.anchor.offsetTop,
        height = item.anchor.offsetHeight;
      if (i === 0) {
        path.push('M', x, y, 'L', x, y + height);
        item.pathStart = 0;
      } else {
        if (pathIndent !== x) {
          path.push('L', pathIndent, y);
        }
        path.push('L', x, y);
        this.navPath.setAttribute('d', path.join(' '));
        item.pathStart = this.navPath.getTotalLength() || 0;
        path.push('L', x, y + height);
      }
      pathIndent = x;
      this.navPath.setAttribute('d', path.join(' '));
      item.pathEnd = this.navPath.getTotalLength();
    });
  }
  syncPath() {
    const someElsAreVisible = () => this.querySelectorAll('.is-visible').length > 0,
      thisElIsVisible = (el) => el.classList.contains('is-visible'),
      pathLength = this.navPath.getTotalLength();
    let pathStart = pathLength,
      pathEnd = 0;
    this.navItems.forEach((item) => {
      if (thisElIsVisible(item.listItem)) {
        pathStart = Math.min(item.pathStart, pathStart);
        pathEnd = Math.max(item.pathEnd, pathEnd);
      }
    });
    if (someElsAreVisible() && pathStart < pathEnd) {
      if (pathStart !== this.lastPathStart || pathEnd !== this.lastPathEnd) {
        const dashArray = `1 ${pathStart} ${pathEnd - pathStart} ${pathLength}`;
        this.navPath.style.setProperty('stroke-dashoffset', '1');
        this.navPath.style.setProperty('stroke-dasharray', dashArray);
        this.navPath.style.setProperty('opacity', '1');
      }
    } else {
      this.navPath.style.setProperty('opacity', '0');
    }
    this.lastPathStart = pathStart;
    this.lastPathEnd = pathEnd;
  }
  markVisibleSection() {
    this.navListItems.forEach((item) => item.classList.remove('is-visible'));
    for (const [index, elementToObserve] of this.elementsToObserve.entries()) {
      const boundingClientRect = elementToObserve.getBoundingClientRect();
      if (boundingClientRect.top > getStickyHeaderOffset() || index === this.elementsToObserve.length - 1) {
        this.querySelector(`a[href="#${elementToObserve.id}"]`).parentElement.classList.add('is-visible');
        break;
      }
    }
    this.syncPath();
  }
};
window.customElements.define('scroll-spy', ScrollSpy);

// js/custom-element/behavior/scroll-shadow.js
var template = `
  <style>
    :host {
      display: inline-block;
      contain: layout;
      position: relative;
    }

    :host([hidden]) {
      display: none;
    }

    s {
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
      pointer-events: none;
      background-image:
        var(--scroll-shadow-top, radial-gradient(farthest-side at 50% 0%, rgba(0,0,0,.2), rgba(0,0,0,0))),
        var(--scroll-shadow-bottom, radial-gradient(farthest-side at 50% 100%, rgba(0,0,0,.2), rgba(0,0,0,0))),
        var(--scroll-shadow-left, radial-gradient(farthest-side at 0%, rgba(0,0,0,.2), rgba(0,0,0,0))),
        var(--scroll-shadow-right, radial-gradient(farthest-side at 100%, rgba(0,0,0,.2), rgba(0,0,0,0)));
      background-position: top, bottom, left, right;
      background-repeat: no-repeat;
      background-size: 100% var(--top, 0), 100% var(--bottom, 0), var(--left, 0) 100%, var(--right, 0) 100%;
    }
  </style>
  <slot></slot>
  <s></s>
`;
var Updater = class {
  constructor(targetElement) {
    this.scheduleUpdate = throttle(() => this.update(targetElement, getComputedStyle(targetElement)));
    this.resizeObserver = new ResizeObserver(this.scheduleUpdate.bind(this));
  }
  start(element) {
    if (this.element) {
      this.stop();
    }
    if (element) {
      element.addEventListener('scroll', this.scheduleUpdate);
      this.resizeObserver.observe(element);
      this.element = element;
    }
  }
  stop() {
    if (!this.element) {
      return;
    }
    this.element.removeEventListener('scroll', this.scheduleUpdate);
    this.resizeObserver.unobserve(this.element);
    this.element = null;
  }
  update(targetElement, style) {
    if (!this.element) {
      return;
    }
    const maxSize = style.getPropertyValue('--scroll-shadow-size') ? parseInt(style.getPropertyValue('--scroll-shadow-size')) : 0;
    const scroll = {
      top: Math.max(this.element.scrollTop, 0),
      bottom: Math.max(this.element.scrollHeight - this.element.offsetHeight - this.element.scrollTop, 0),
      left: Math.max(this.element.scrollLeft, 0),
      right: Math.max(this.element.scrollWidth - this.element.offsetWidth - this.element.scrollLeft, 0),
    };
    requestAnimationFrame(() => {
      for (const position of ['top', 'bottom', 'left', 'right']) {
        targetElement.style.setProperty(`--${position}`, `${scroll[position] > maxSize ? maxSize : scroll[position]}px`);
      }
    });
  }
};
var ScrollShadow = class extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' }).innerHTML = template;
    this.updater = new Updater(this.shadowRoot.lastElementChild);
  }
  connectedCallback() {
    this.shadowRoot.querySelector('slot').addEventListener('slotchange', () => this.start());
    this.start();
  }
  disconnectedCallback() {
    this.updater.stop();
  }
  start() {
    this.updater.start(this.firstElementChild);
  }
};
if ('ResizeObserver' in window) {
  window.customElements.define('scroll-shadow', ScrollShadow);
}

// js/custom-element/behavior/share-toggle-button.js
var ShareToggleButton = class extends ToggleButton {
  _onButtonClick() {
    if (window.matchMedia(window.themeVariables.breakpoints.phone).matches && navigator.share) {
      navigator.share({
        title: this.hasAttribute('share-title') ? this.getAttribute('share-title') : document.title,
        url: this.hasAttribute('share-url') ? this.getAttribute('share-url') : window.location.href,
      });
    } else {
      super._onButtonClick();
    }
  }
};
window.customElements.define('share-toggle-button', ShareToggleButton, { extends: 'button' });

// js/custom-element/ui/drag-cursor.js
var DragCursor = class extends HTMLElement {
  connectedCallback() {
    this.scrollableElement = this.parentElement;
    this.scrollableElement.addEventListener('mouseenter', this._onMouseEnter.bind(this));
    this.scrollableElement.addEventListener('mousemove', this._onMouseMove.bind(this));
    this.scrollableElement.addEventListener('mouseleave', this._onMouseLeave.bind(this));
    this.innerHTML = `
      <svg fill="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 120 120">
        <path d="M0 60C0 26.863 26.863 0 60 0s60 26.863 60 60-26.863 60-60 60S0 93.137 0 60z" fill="rgb(var(--text-color))"/>
        <path d="M46 50L36 60l10 10M74 50l10 10-10 10" stroke="rgb(var(--section-background))" stroke-width="4"/>
      </svg>
    `;
  }
  _onMouseEnter(event) {
    this.removeAttribute('hidden');
    this._positionCursor(event);
  }
  _onMouseLeave() {
    this.setAttribute('hidden', '');
  }
  _onMouseMove(event) {
    this.toggleAttribute('hidden', event.target.tagName === 'BUTTON' || event.target.tagName === 'A');
    this._positionCursor(event);
  }
  _positionCursor(event) {
    const elementBoundingRect = this.scrollableElement.getBoundingClientRect();
    const x = event.clientX - elementBoundingRect.x;
    const y = event.clientY - elementBoundingRect.y;
    this.style.transform = `translate(${x - this.clientWidth / 2}px, ${y - this.clientHeight / 2}px)`;
  }
};
window.customElements.define('drag-cursor', DragCursor);

// js/custom-element/ui/scrollable-content.js
var ScrollableContent = class extends CustomHTMLElement {
  connectedCallback() {
    if (this.draggable) {
      this._setupDraggability();
    }
    this._checkScrollability();
    window.addEventListener('resize', this._checkScrollability.bind(this));
    this.addEventListener('scroll', throttle(this._calculateProgress.bind(this), 15));
  }
  get draggable() {
    return this.hasAttribute('draggable');
  }
  _setupDraggability() {
    this.insertAdjacentHTML('afterend', '<drag-cursor hidden class="custom-drag-cursor"></drag-cursor>');
    const mediaQuery = matchMedia('(hover: none)');
    mediaQuery.addListener(this._onMediaChanges.bind(this));
    if (!mediaQuery.matches) {
      this._attachDraggableListeners();
    }
  }
  _attachDraggableListeners() {
    this.delegate.on('mousedown', this._onMouseDown.bind(this));
    this.delegate.on('mousemove', this._onMouseMove.bind(this));
    this.delegate.on('mouseup', this._onMouseUp.bind(this));
  }
  _removeDraggableListeners() {
    this.delegate.off('mousedown');
    this.delegate.off('mousemove');
    this.delegate.off('mouseup');
  }
  _checkScrollability() {
    this.classList.toggle('is-scrollable', this.scrollWidth > this.offsetWidth);
  }
  _calculateProgress() {
    const scrollLeft = this.scrollLeft * (window.themeVariables.settings.direction === 'ltr' ? 1 : -1);
    const progress = Math.max(0, Math.min(1, scrollLeft / (this.scrollWidth - this.clientWidth))) * 100;
    triggerEvent(this, 'scrollable-content:progress', { progress });
  }
  _onMediaChanges(event) {
    if (!event.matches) {
      this._attachDraggableListeners();
    } else {
      this._removeDraggableListeners();
    }
  }
  _onMouseDown(event) {
    if (event.target && event.target.nodeName === 'IMG') {
      event.preventDefault();
    }
    this.startX = event.clientX + this.scrollLeft;
    this.diffX = 0;
    this.drag = true;
  }
  _onMouseMove(event) {
    if (this.drag) {
      this.diffX = this.startX - (event.clientX + this.scrollLeft);
      this.scrollLeft += this.diffX;
    }
  }
  _onMouseUp() {
    this.drag = false;
    let start = 1;
    let animate = () => {
      let step = Math.sinh(start);
      if (step <= 0) {
        window.cancelAnimationFrame(animate);
      } else {
        this.scrollLeft += this.diffX * step;
        start -= 0.03;
        window.requestAnimationFrame(animate);
      }
    };
    animate();
  }
};
window.customElements.define('scrollable-content', ScrollableContent);

// js/custom-element/ui/loading-bar.js
var LoadingBar = class extends CustomHTMLElement {
  constructor() {
    super();
    this.rootDelegate.on('theme:loading:start', this.show.bind(this));
    this.rootDelegate.on('theme:loading:end', this.hide.bind(this));
    this.delegate.on('transitionend', this._onTransitionEnd.bind(this));
  }
  show() {
    this.classList.add('is-visible');
    this.style.transform = 'scaleX(0.4)';
  }
  hide() {
    this.style.transform = 'scaleX(1)';
    this.classList.add('is-finished');
  }
  _onTransitionEnd(event) {
    if (event.propertyName === 'transform' && this.classList.contains('is-finished')) {
      this.classList.remove('is-visible');
      this.classList.remove('is-finished');
      this.style.transform = 'scaleX(0)';
    }
  }
};
window.customElements.define('loading-bar', LoadingBar);

// js/custom-element/ui/split-lines.js
var SplitLines = class extends HTMLElement {
  connectedCallback() {
    this.originalContent = this.textContent;
    this.lastWidth = window.innerWidth;
    this.hasBeenSplitted = false;
    window.addEventListener('resize', this._onResize.bind(this));
  }
  [Symbol.asyncIterator]() {
    return {
      splitPromise: this.split.bind(this),
      index: 0,
      async next() {
        const lines = await this.splitPromise();
        if (this.index !== lines.length) {
          return { done: false, value: lines[this.index++] };
        } else {
          return { done: true };
        }
      },
    };
  }
  split(force = false) {
    if (this.childElementCount > 0 && !force) {
      return Promise.resolve(Array.from(this.children));
    }
    this.hasBeenSplitted = true;
    return new Promise((resolve) => {
      requestAnimationFrame(() => {
        this.innerHTML = this.originalContent.replace(/./g, '<span>$&</span>').replace(/\s/g, ' ');
        const bounds = {};
        Array.from(this.children).forEach((child) => {
          const rect = parseInt(child.getBoundingClientRect().top);
          bounds[rect] = (bounds[rect] || '') + child.textContent;
        });
        this.innerHTML = Object.values(bounds)
          .map((item) => `<span ${this.hasAttribute('reveal') && !force ? 'reveal' : ''} ${this.hasAttribute('reveal-visibility') && !force ? 'reveal-visibility' : ''} style="display: block">${item.trim()}</span>`)
          .join('');
        this.style.opacity = this.hasAttribute('reveal') ? 1 : null;
        this.style.visibility = this.hasAttribute('reveal-visibility') ? 'visible' : null;
        resolve(Array.from(this.children));
      });
    });
  }
  async _onResize() {
    if (this.lastWidth === window.innerWidth || !this.hasBeenSplitted) {
      return;
    }
    await this.split(true);
    this.dispatchEvent(new CustomEvent('split-lines:re-split', { bubbles: true }));
    this.lastWidth = window.innerWidth;
  }
};
window.customElements.define('split-lines', SplitLines);

// js/custom-element/ui/popover.js
var PopoverContent = class extends OpenableElement {
  connectedCallback() {
    super.connectedCallback();
    this.delegate.on('click', '.popover__overlay', () => (this.open = false));
  }
  attributeChangedCallback(name, oldValue, newValue) {
    super.attributeChangedCallback(name, oldValue, newValue);
    switch (name) {
      case 'open':
        document.documentElement.classList.toggle('lock-mobile', this.open);
    }
  }
};
window.customElements.define('popover-content', PopoverContent);

// js/custom-element/ui/tabs-nav.js
var TabsNav = class extends HTMLElement {
  connectedCallback() {
    this.buttons = Array.from(this.querySelectorAll('button[aria-controls]'));
    this.scrollerElement = this.querySelector('.tabs-nav__scroller');
    this.buttons.forEach((button) => button.addEventListener('click', () => this.selectButton(button)));
    this.addEventListener('shopify:block:select', (event) => this.selectButton(event.target, !event.detail.load));
    this.positionElement = document.createElement('span');
    this.positionElement.classList.add('tabs-nav__position');
    this.buttons[0].parentElement.insertAdjacentElement('afterend', this.positionElement);
    window.addEventListener('resize', this._onWindowResized.bind(this));
    this._adjustNavigationPosition();
    if (this.hasArrows) {
      this._handleArrows();
    }
  }
  get hasArrows() {
    return this.hasAttribute('arrows');
  }
  get selectedTabIndex() {
    return this.buttons.findIndex((button) => button.getAttribute('aria-expanded') === 'true');
  }
  get selectedButton() {
    return this.buttons.find((button) => button.getAttribute('aria-expanded') === 'true');
  }
  selectButton(button, animate = true) {
    if (!this.buttons.includes(button) || this.selectedButton === button) {
      return;
    }
    const from = document.getElementById(this.selectedButton.getAttribute('aria-controls')),
      to = document.getElementById(button.getAttribute('aria-controls'));
    if (animate) {
      this._transitionContent(from, to);
    } else {
      from.hidden = true;
      to.hidden = false;
    }
    this.selectedButton.setAttribute('aria-expanded', 'false');
    button.setAttribute('aria-expanded', 'true');
    triggerEvent(this, 'tabs-nav:changed', { button });
    this._adjustNavigationPosition();
  }
  addButton(button) {
    button.addEventListener('click', () => this.selectButton(button));
    button.setAttribute('aria-expanded', 'false');
    this.buttons[this.buttons.length - 1].insertAdjacentElement('afterend', button);
    this.buttons.push(button);
    this._adjustNavigationPosition(false);
  }
  _transitionContent(from, to) {
    from.animate(
      {
        opacity: [1, 0],
      },
      {
        duration: 250,
        easing: 'ease',
      }
    ).onfinish = () => {
      from.hidden = true;
      to.hidden = false;
      to.animate(
        {
          opacity: [0, 1],
        },
        {
          duration: 250,
          easing: 'ease',
        }
      );
    };
  }
  _onWindowResized() {
    this._adjustNavigationPosition();
  }
  _adjustNavigationPosition(shouldAnimate = true) {
    const scale = this.selectedButton.clientWidth / this.positionElement.parentElement.clientWidth,
      translate = this.selectedButton.offsetLeft / this.positionElement.parentElement.clientWidth / scale,
      windowHalfWidth = this.scrollerElement.clientWidth / 2;
    this.scrollerElement.scrollTo({
      behavior: shouldAnimate ? 'smooth' : 'auto',
      left: this.selectedButton.offsetLeft - windowHalfWidth + this.selectedButton.clientWidth / 2,
    });
    if (!shouldAnimate) {
      this.positionElement.style.transition = 'none';
    }
    this.positionElement.style.setProperty('--scale', scale);
    this.positionElement.style.setProperty('--translate', `${translate * 100}%`);
    this.positionElement.clientWidth;
    requestAnimationFrame(() => {
      this.positionElement.classList.add('is-initialized');
      this.positionElement.style.transition = null;
    });
  }
  /**
   * When the tabs nav can have lot of tab items (for instance on product page), the custom element can add
   * extra arrows to make navigation easier
   */
  _handleArrows() {
    const arrowsContainer = this.querySelector('.tabs-nav__arrows');
    arrowsContainer.firstElementChild.addEventListener('click', () => {
      this.selectButton(this.buttons[Math.max(this.selectedTabIndex - 1, 0)]);
    });
    arrowsContainer.lastElementChild.addEventListener('click', () => {
      this.selectButton(this.buttons[Math.min(this.selectedTabIndex + 1, this.buttons.length - 1)]);
    });
  }
};
window.customElements.define('tabs-nav', TabsNav);

// js/helper/library-loader.js
var LibraryLoader = class {
  static load(libraryName) {
    const STATUS_REQUESTED = 'requested',
      STATUS_LOADED = 'loaded';
    const library = this.libraries[libraryName];
    if (!library) {
      return;
    }
    if (library.status === STATUS_REQUESTED) {
      return library.promise;
    }
    if (library.status === STATUS_LOADED) {
      return Promise.resolve();
    }
    let promise;
    if (library.type === 'script') {
      promise = new Promise((resolve, reject) => {
        let tag = document.createElement('script');
        tag.id = library.tagId;
        tag.src = library.src;
        tag.onerror = reject;
        tag.onload = () => {
          library.status = STATUS_LOADED;
          resolve();
        };
        document.body.appendChild(tag);
      });
    } else {
      promise = new Promise((resolve, reject) => {
        let tag = document.createElement('link');
        tag.id = library.tagId;
        tag.href = library.src;
        tag.rel = 'stylesheet';
        tag.type = 'text/css';
        tag.onerror = reject;
        tag.onload = () => {
          library.status = STATUS_LOADED;
          resolve();
        };
        document.body.appendChild(tag);
      });
    }
    library.promise = promise;
    library.status = STATUS_REQUESTED;
    return promise;
  }
};
__publicField(LibraryLoader, 'libraries', {
  flickity: {
    tagId: 'flickity',
    src: window.themeVariables.libs.flickity,
    type: 'script',
  },
  photoswipe: {
    tagId: 'photoswipe',
    src: window.themeVariables.libs.photoswipe,
    type: 'script',
  },
  qrCode: {
    tagId: 'qrCode',
    src: window.themeVariables.libs.qrCode,
    type: 'script',
  },
  modelViewerUiStyles: {
    tagId: 'shopify-model-viewer-ui-styles',
    src: 'https://cdn.shopify.com/shopifycloud/model-viewer-ui/assets/v1.0/model-viewer-ui.css',
    type: 'link',
  },
});

// js/custom-element/ui/qr-code.js
var QrCode = class extends HTMLElement {
  async connectedCallback() {
    await LibraryLoader.load('qrCode');
    new window.QRCode(this, {
      text: this.getAttribute('identifier'),
      width: 200,
      height: 200,
    });
  }
};
window.customElements.define('qr-code', QrCode);

// js/custom-element/ui/country-selector.js
var CountrySelector = class extends HTMLSelectElement {
  connectedCallback() {
    this.provinceElement = document.getElementById(this.getAttribute('aria-owns'));
    this.addEventListener('change', this._updateProvinceVisibility.bind(this));
    if (this.hasAttribute('data-default')) {
      for (let i = 0; i !== this.options.length; ++i) {
        if (this.options[i].text === this.getAttribute('data-default')) {
          this.selectedIndex = i;
          break;
        }
      }
    }
    this._updateProvinceVisibility();
    const provinceSelectElement = this.provinceElement.tagName === 'SELECT' ? this.provinceElement : this.provinceElement.querySelector('select');
    if (provinceSelectElement.hasAttribute('data-default')) {
      for (let i = 0; i !== provinceSelectElement.options.length; ++i) {
        if (provinceSelectElement.options[i].text === provinceSelectElement.getAttribute('data-default')) {
          provinceSelectElement.selectedIndex = i;
          break;
        }
      }
    }
  }
  _updateProvinceVisibility() {
    const selectedOption = this.options[this.selectedIndex];
    if (!selectedOption) {
      return;
    }
    let provinces = JSON.parse(selectedOption.getAttribute('data-provinces') || '[]'),
      provinceSelectElement = this.provinceElement.tagName === 'SELECT' ? this.provinceElement : this.provinceElement.querySelector('select');
    provinceSelectElement.innerHTML = '';
    if (provinces.length === 0) {
      this.provinceElement.hidden = true;
      return;
    }
    provinces.forEach((data) => {
      provinceSelectElement.options.add(new Option(data[1], data[0]));
    });
    this.provinceElement.hidden = false;
  }
};
window.customElements.define('country-selector', CountrySelector, { extends: 'select' });

// js/custom-element/ui/modal.js
var ModalContent = class extends OpenableElement {
  connectedCallback() {
    super.connectedCallback();
    if (this.appearAfterDelay && !(this.onlyOnce && this.hasAppearedOnce)) {
      setTimeout(() => (this.open = true), this.apparitionDelay);
    }
    this.delegate.on('click', '.modal__overlay', () => (this.open = false));
  }
  get appearAfterDelay() {
    return this.hasAttribute('apparition-delay');
  }
  get apparitionDelay() {
    return parseInt(this.getAttribute('apparition-delay') || 0) * 1e3;
  }
  get onlyOnce() {
    return this.hasAttribute('only-once');
  }
  get hasAppearedOnce() {
    return localStorage.getItem('theme:popup-appeared') !== null;
  }
  attributeChangedCallback(name, oldValue, newValue) {
    super.attributeChangedCallback(name, oldValue, newValue);
    switch (name) {
      case 'open':
        document.documentElement.classList.toggle('lock-all', this.open);
        if (this.open) {
          localStorage.setItem('theme:popup-appeared', true);
        }
    }
  }
};
window.customElements.define('modal-content', ModalContent);

// js/custom-element/ui/price-range.js
var PriceRange = class extends HTMLElement {
  connectedCallback() {
    this.rangeLowerBound = this.querySelector('.price-range__range-group input:first-child');
    this.rangeHigherBound = this.querySelector('.price-range__range-group input:last-child');
    this.textInputLowerBound = this.querySelector('.price-range__input:first-child input');
    this.textInputHigherBound = this.querySelector('.price-range__input:last-child input');
    this.textInputLowerBound.addEventListener('focus', () => this.textInputLowerBound.select());
    this.textInputHigherBound.addEventListener('focus', () => this.textInputHigherBound.select());
    this.textInputLowerBound.addEventListener('change', (event) => {
      event.target.value = Math.max(Math.min(parseInt(event.target.value), parseInt(this.textInputHigherBound.value || event.target.max) - 1), event.target.min);
      this.rangeLowerBound.value = event.target.value;
      this.rangeLowerBound.parentElement.style.setProperty('--range-min', `${(parseInt(this.rangeLowerBound.value) / parseInt(this.rangeLowerBound.max)) * 100}%`);
    });
    this.textInputHigherBound.addEventListener('change', (event) => {
      event.target.value = Math.min(Math.max(parseInt(event.target.value), parseInt(this.textInputLowerBound.value || event.target.min) + 1), event.target.max);
      this.rangeHigherBound.value = event.target.value;
      this.rangeHigherBound.parentElement.style.setProperty('--range-max', `${(parseInt(this.rangeHigherBound.value) / parseInt(this.rangeHigherBound.max)) * 100}%`);
    });
    this.rangeLowerBound.addEventListener('change', (event) => {
      this.textInputLowerBound.value = event.target.value;
      this.textInputLowerBound.dispatchEvent(new Event('change', { bubbles: true }));
    });
    this.rangeHigherBound.addEventListener('change', (event) => {
      this.textInputHigherBound.value = event.target.value;
      this.textInputHigherBound.dispatchEvent(new Event('change', { bubbles: true }));
    });
    this.rangeLowerBound.addEventListener('input', (event) => {
      triggerEvent(this, 'facet:abort-loading');
      event.target.value = Math.min(parseInt(event.target.value), parseInt(this.textInputHigherBound.value || event.target.max) - 1);
      event.target.parentElement.style.setProperty('--range-min', `${(parseInt(event.target.value) / parseInt(event.target.max)) * 100}%`);
      this.textInputLowerBound.value = event.target.value;
    });
    this.rangeHigherBound.addEventListener('input', (event) => {
      triggerEvent(this, 'facet:abort-loading');
      event.target.value = Math.max(parseInt(event.target.value), parseInt(this.textInputLowerBound.value || event.target.min) + 1);
      event.target.parentElement.style.setProperty('--range-max', `${(parseInt(event.target.value) / parseInt(event.target.max)) * 100}%`);
      this.textInputHigherBound.value = event.target.value;
    });
  }
};
window.customElements.define('price-range', PriceRange);

// js/custom-element/ui/link-bar.js
var LinkBar = class extends HTMLElement {
  connectedCallback() {
    const selectedItem = this.querySelector('.link-bar__link-item--selected');
    if (selectedItem) {
      requestAnimationFrame(() => {
        selectedItem.style.scrollSnapAlign = 'none';
      });
    }
  }
};
window.customElements.define('link-bar', LinkBar);

// js/helper/media-features.js
var MediaFeatures = class {
  static prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }
  static supportsHover() {
    return window.matchMedia('(pointer: fine)').matches;
  }
};

// js/custom-element/ui/flickity-carousel.js
var FlickityCarousel = class extends CustomHTMLElement {
  constructor() {
    super();
    if (this.childElementCount === 1) {
      return;
    }
    this.addEventListener('flickity:ready', this._preloadNextImage.bind(this));
    this.addEventListener('flickity:slide-changed', this._preloadNextImage.bind(this));
    this._createFlickity();
  }
  async disconnectedCallback() {
    if (this.flickity) {
      const flickityInstance = await this.flickity;
      flickityInstance.destroy();
    }
  }
  get flickityConfig() {
    return JSON.parse(this.getAttribute('flickity-config'));
  }
  get flickityInstance() {
    return this.flickity;
  }
  async next() {
    (await this.flickityInstance).next();
  }
  async previous() {
    (await this.flickityInstance).previous();
  }
  async select(indexOrSelector) {
    (await this.flickityInstance).selectCell(indexOrSelector);
  }
  async setDraggable(draggable) {
    const flickityInstance = await this.flickity;
    flickityInstance.options.draggable = draggable;
    flickityInstance.updateDraggable();
  }
  async reload() {
    const flickityInstance = await this.flickity;
    flickityInstance.destroy();
    if (this.flickityConfig['cellSelector']) {
      Array.from(this.children)
        .sort((a, b) => (parseInt(a.getAttribute('data-original-position')) > parseInt(b.getAttribute('data-original-position')) ? 1 : -1))
        .forEach((node) => this.appendChild(node));
    }
    this._createFlickity();
  }
  async _createFlickity() {
    this.flickity = new Promise(async (resolve) => {
      await LibraryLoader.load('flickity');
      await this.untilVisible({ rootMargin: '400px', threshold: 0 });
      const flickityInstance = new window.ThemeFlickity(this, {
        ...this.flickityConfig,
        ...{
          rightToLeft: window.themeVariables.settings.direction === 'rtl',
          accessibility: MediaFeatures.supportsHover(),
          // Flickity cause the carousel to scroll when focused, which is annoying on mobile
          on: {
            ready: (event) => triggerEvent(this, 'flickity:ready', event),
            change: (event) => triggerEvent(this, 'flickity:slide-changed', event),
            settle: (event) => triggerEvent(this, 'flickity:slide-settled', event),
          },
        },
      });
      resolve(flickityInstance);
    });
    if (this.hasAttribute('click-nav')) {
      const flickityInstance = await this.flickityInstance;
      flickityInstance.on('staticClick', this._onStaticClick.bind(this));
      this.addEventListener('mousemove', this._onMouseMove.bind(this));
    }
  }
  /**
   * If the "click-nav" option is passed, desktop device can switch from one slide to the other
   */
  async _onStaticClick(event, pointer, cellElement) {
    const flickityInstance = await this.flickityInstance,
      isVideoOrModelType = flickityInstance.selectedElement.hasAttribute('data-media-type') && ['video', 'external_video', 'model'].includes(flickityInstance.selectedElement.getAttribute('data-media-type'));
    if (!cellElement || isVideoOrModelType || window.matchMedia(window.themeVariables.breakpoints.phone).matches) {
      return;
    }
    const flickityViewport = flickityInstance.viewport,
      boundingRect = flickityViewport.getBoundingClientRect(),
      halfEdge = Math.floor(boundingRect.right - boundingRect.width / 2);
    if (pointer.clientX > halfEdge) {
      flickityInstance.next();
    } else {
      flickityInstance.previous();
    }
  }
  /**
   * Add the class "is-hovering-right" or "is-hovering-left" depending on the part we're hovering
   */
  async _onMouseMove(event) {
    const flickityInstance = await this.flickityInstance,
      isVideoOrModelType = flickityInstance.selectedElement.hasAttribute('data-media-type') && ['video', 'external_video', 'model'].includes(flickityInstance.selectedElement.getAttribute('data-media-type'));
    this.classList.toggle('is-hovering-right', event.offsetX > this.clientWidth / 2 && !isVideoOrModelType);
    this.classList.toggle('is-hovering-left', event.offsetX <= this.clientWidth / 2 && !isVideoOrModelType);
  }
  /**
   * To give a feeling of faster browsing, we always preload the next image once we settle on a slide
   */
  async _preloadNextImage() {
    const flickityInstance = await this.flickity;
    if (flickityInstance.selectedElement.nextElementSibling) {
      flickityInstance.selectedElement.nextElementSibling.querySelector('img')?.setAttribute('loading', 'eager');
    }
  }
};
window.customElements.define('flickity-carousel', FlickityCarousel);

// js/helper/dom.js
function deepQuerySelector(root, selector) {
  let element = root.querySelector(selector);
  if (element) {
    return element;
  }
  for (const template2 of root.querySelectorAll('template')) {
    element = deepQuerySelector(template2.content, selector);
    if (element) {
      return element;
    }
  }
  return null;
}
function getSiblings(element, filter, includeSelf = false) {
  let siblings = [];
  let currentElement = element;
  while ((currentElement = currentElement.previousElementSibling)) {
    if (!filter || currentElement.matches(filter)) {
      siblings.push(currentElement);
    }
  }
  if (includeSelf) {
    siblings.push(element);
  }
  currentElement = element;
  while ((currentElement = currentElement.nextElementSibling)) {
    if (!filter || currentElement.matches(filter)) {
      siblings.push(currentElement);
    }
  }
  return siblings;
}
async function resolveAsyncIterator(target) {
  const processedTarget = [];
  if (!(target != null && typeof target[Symbol.iterator] === 'function')) {
    target = [target];
  }
  for (const targetItem of target) {
    if (typeof targetItem[Symbol.asyncIterator] === 'function') {
      for await (const awaitTarget of targetItem) {
        processedTarget.push(awaitTarget);
      }
    } else {
      processedTarget.push(targetItem);
    }
  }
  return processedTarget;
}

// js/custom-element/ui/flickity-controls.js
var FlickityControls = class extends CustomHTMLElement {
  async connectedCallback() {
    this.flickityCarousel.addEventListener('flickity:ready', this._onSlideChanged.bind(this, false));
    this.flickityCarousel.addEventListener('flickity:slide-changed', this._onSlideChanged.bind(this, true));
    this.delegate.on('click', '[data-action="prev"]', () => this.flickityCarousel.previous());
    this.delegate.on('click', '[data-action="next"]', () => this.flickityCarousel.next());
    this.delegate.on('click', '[data-action="select"]', (event, target) => this.flickityCarousel.select(`#${target.getAttribute('aria-controls')}`));
  }
  get flickityCarousel() {
    return (this._flickityCarousel = this._flickityCarousel || document.getElementById(this.getAttribute('controls')));
  }
  async _onSlideChanged(animate = true) {
    let flickityInstance = await this.flickityCarousel.flickityInstance,
      activeItems = Array.from(this.querySelectorAll(`[aria-controls="${flickityInstance.selectedElement.id}"]`));
    activeItems.forEach((activeItem) => {
      activeItem.setAttribute('aria-current', 'true');
      getSiblings(activeItem).forEach((sibling) => sibling.removeAttribute('aria-current'));
      requestAnimationFrame(() => {
        if (activeItem.offsetParent && activeItem.offsetParent !== this) {
          const windowHalfHeight = activeItem.offsetParent.clientHeight / 2,
            windowHalfWidth = activeItem.offsetParent.clientWidth / 2;
          activeItem.offsetParent.scrollTo({
            behavior: animate ? 'smooth' : 'auto',
            top: activeItem.offsetTop - windowHalfHeight + activeItem.clientHeight / 2,
            left: activeItem.offsetLeft - windowHalfWidth + activeItem.clientWidth / 2,
          });
        }
      });
    });
  }
};
window.customElements.define('flickity-controls', FlickityControls);

// js/custom-element/ui/external-video.js
var ExternalVideo = class extends CustomHTMLElement {
  /**
   * This must be done in the constructor and not connectedCallback because the element will be re-added
   * at run-time by Flickity
   */
  constructor() {
    super();
    this.hasLoaded = false;
    (async () => {
      if (this.autoPlay) {
        await this.untilVisible({ rootMargin: '300px', threshold: 0 });
        this.play();
      } else {
        this.addEventListener('click', this.play.bind(this), { once: true });
      }
    })();
  }
  get autoPlay() {
    return this.hasAttribute('autoplay');
  }
  get provider() {
    return this.getAttribute('provider');
  }
  async play() {
    if (!this.hasLoaded) {
      await this._setupPlayer();
    }
    if (this.provider === 'youtube') {
      setTimeout(() => {
        this.querySelector('iframe').contentWindow.postMessage(JSON.stringify({ event: 'command', func: 'playVideo', args: '' }), '*');
      }, 150);
    } else if (this.provider === 'vimeo') {
      this.querySelector('iframe').contentWindow.postMessage(JSON.stringify({ method: 'play' }), '*');
    }
  }
  pause() {
    if (!this.hasLoaded) {
      return;
    }
    if (this.provider === 'youtube') {
      this.querySelector('iframe').contentWindow.postMessage(JSON.stringify({ event: 'command', func: 'pauseVideo', args: '' }), '*');
    } else if (this.provider === 'vimeo') {
      this.querySelector('iframe').contentWindow.postMessage(JSON.stringify({ method: 'pause' }), '*');
    }
  }
  _setupPlayer() {
    if (this._setupPromise) {
      return this._setupPromise;
    }
    return (this._setupPromise = new Promise((resolve) => {
      const template2 = this.querySelector('template'),
        node = template2.content.firstElementChild.cloneNode(true);
      node.onload = () => {
        this.hasLoaded = true;
        resolve();
      };
      if (this.autoPlay) {
        template2.replaceWith(node);
      } else {
        this.innerHTML = '';
        this.appendChild(node);
      }
    }));
  }
};
window.customElements.define('external-video', ExternalVideo);

// js/helper/product-loader.js
var ProductLoader = class {
  static load(productHandle) {
    if (!productHandle) {
      return;
    }
    if (this.loadedProducts[productHandle]) {
      return this.loadedProducts[productHandle];
    }
    this.loadedProducts[productHandle] = new Promise(async (resolve) => {
      const response = await fetch(`${window.themeVariables.routes.rootUrlWithoutSlash}/products/${productHandle}.js`);
      const responseAsJson = await response.json();
      resolve(responseAsJson);
    });
    return this.loadedProducts[productHandle];
  }
};
__publicField(ProductLoader, 'loadedProducts', {});

// js/custom-element/ui/model-media.js
var ModelMedia = class extends HTMLElement {
  /**
   * This must be done in the constructor and not connectedCallback because the element will be re-added
   * at run-time by Flickity
   */
  constructor() {
    super();
    LibraryLoader.load('modelViewerUiStyles');
    window.Shopify.loadFeatures([
      {
        name: 'shopify-xr',
        version: '1.0',
        onLoad: this._setupShopifyXr.bind(this),
      },
      {
        name: 'model-viewer-ui',
        version: '1.0',
        onLoad: () => {
          this.modelUi = new window.Shopify.ModelViewerUI(this.firstElementChild, { focusOnPlay: false });
          const modelViewer = this.querySelector('model-viewer');
          modelViewer.addEventListener('shopify_model_viewer_ui_toggle_play', () => {
            modelViewer.dispatchEvent(new CustomEvent('model:played', { bubbles: true }));
          });
          modelViewer.addEventListener('shopify_model_viewer_ui_toggle_pause', () => {
            modelViewer.dispatchEvent(new CustomEvent('model:paused', { bubbles: true }));
          });
        },
      },
    ]);
  }
  disconnectedCallback() {
    this.modelUi?.destroy();
  }
  play() {
    if (this.modelUi) {
      this.modelUi.play();
    }
  }
  pause() {
    if (this.modelUi) {
      this.modelUi.pause();
    }
  }
  async _setupShopifyXr() {
    if (!window.ShopifyXR) {
      document.addEventListener('shopify_xr_initialized', this._setupShopifyXr.bind(this));
    } else {
      const product = await ProductLoader.load(this.getAttribute('product-handle'));
      const models = product['media'].filter((media) => media['media_type'] === 'model');
      window.ShopifyXR.addModels(models);
      window.ShopifyXR.setupXRElements();
    }
  }
};
window.customElements.define('model-media', ModelMedia);

// js/custom-element/ui/combo-box.js
var ComboBox = class extends OpenableElement {
  connectedCallback() {
    super.connectedCallback();
    this.options = Array.from(this.querySelectorAll('[role="option"]'));
    this.delegate.on('click', '[role="option"]', this._onValueClicked.bind(this));
    this.delegate.on('keydown', '[role="listbox"]', this._onKeyDown.bind(this));
    this.delegate.on('change', 'select', this._onValueChanged.bind(this));
    this.delegate.on('click', '.combo-box__overlay', () => (this.open = false));
    if (this.hasAttribute('fit-toggle')) {
      const maxWidth = Math.max(...this.options.map((item) => item.clientWidth)),
        control = document.querySelector(`[aria-controls="${this.id}"]`);
      if (control) {
        control.style.setProperty('--largest-option-width', `${maxWidth + 2}px`);
      }
    }
  }
  get nativeSelect() {
    return this.querySelector('select');
  }
  set selectedValue(value) {
    this.options.forEach((option) => {
      option.setAttribute('aria-selected', option.getAttribute('value') === value ? 'true' : 'false');
    });
  }
  attributeChangedCallback(name, oldValue, newValue) {
    super.attributeChangedCallback(name, oldValue, newValue);
    switch (name) {
      case 'open':
        if (this.open) {
          const boundingRect = this.getBoundingClientRect();
          this.classList.toggle('combo-box--top', boundingRect.top >= (window.innerHeight / 2) * 1.5);
          setTimeout(() => this.focusTrap.activate(), 150);
        } else {
          this.focusTrap.deactivate();
          setTimeout(() => this.classList.remove('combo-box--top'), 200);
        }
        document.documentElement.classList.toggle('lock-mobile', this.open);
    }
  }
  // Called when the option of the custom select is clicked
  _onValueClicked(event, target) {
    this.selectedValue = target.value;
    this.nativeSelect.value = target.value;
    this.nativeSelect.dispatchEvent(new Event('change', { bubbles: true }));
    this.open = false;
  }
  // Called when the option of the underlying native select is changed (for instance by external code)
  _onValueChanged(event, target) {
    Array.from(this.nativeSelect.options).forEach((option) => option.toggleAttribute('selected', target.value === option.value));
    this.selectedValue = target.value;
  }
  // Improves accessibility with arrow up/down
  _onKeyDown(event) {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (event.key === 'ArrowDown') {
        document.activeElement.nextElementSibling?.focus();
      } else {
        document.activeElement.previousElementSibling?.focus();
      }
    }
  }
};
window.customElements.define('combo-box', ComboBox);

// js/custom-element/ui/quantity-selector.js
var QuantitySelector = class extends CustomHTMLElement {
  connectedCallback() {
    this.inputElement = this.querySelector('input');
    this.delegate.on('click', 'button:first-child', () => (this.inputElement.quantity = this.inputElement.quantity - 1));
    this.delegate.on('click', 'button:last-child', () => (this.inputElement.quantity = this.inputElement.quantity + 1));
  }
};
window.customElements.define('quantity-selector', QuantitySelector);

// js/custom-element/ui/input-number.js
var InputNumber = class extends HTMLInputElement {
  connectedCallback() {
    this.addEventListener('input', this._onValueInput.bind(this));
    this.addEventListener('change', this._onValueChanged.bind(this));
    this.addEventListener('keydown', this._onKeyDown.bind(this));
  }
  get quantity() {
    return parseInt(this.value);
  }
  set quantity(quantity) {
    const isNumeric = (typeof quantity === 'number' || (typeof quantity === 'string' && quantity.trim() !== '')) && !isNaN(quantity);
    if (quantity === '') {
      return;
    }
    if (!isNumeric || quantity < 0) {
      quantity = parseInt(quantity) || 1;
    }
    this.value = Math.max(this.min || 1, Math.min(quantity, this.max || Number.MAX_VALUE)).toString();
    this.size = Math.max(this.value.length + 1, 2);
  }
  _onValueInput() {
    this.quantity = this.value;
  }
  _onValueChanged() {
    if (this.value === '') {
      this.quantity = 1;
    }
  }
  _onKeyDown(event) {
    event.stopPropagation();
    if (event.key === 'ArrowUp') {
      this.quantity = this.quantity + 1;
    } else if (event.key === 'ArrowDown') {
      this.quantity = this.quantity - 1;
    }
  }
};
window.customElements.define('input-number', InputNumber, { extends: 'input' });

// js/helper/image.js
function getSizedMediaUrl(media, size) {
  let src = typeof media === 'string' ? media : media['preview_image'] ? media['preview_image']['src'] : media['url'];
  if (size === null) {
    return src;
  }
  if (size === 'master') {
    return src.replace(/http(s)?:/, '');
  }
  const match = src.match(/\.(jpg|jpeg|gif|png|bmp|bitmap|tiff|tif|webp)(\?v=\d+)?$/i);
  if (match) {
    const prefix = src.split(match[0]);
    const suffix = match[0];
    return (prefix[0] + '_' + size + suffix).replace(/http(s)?:/, '');
  } else {
    return null;
  }
}
// function getMediaSrcset(media, sizeList) {
//   let srcset = [],
//     supportedSizes = typeof media === 'string' ? sizeList : getSupportedSizes(media, sizeList);
//   supportedSizes.forEach((supportedSize) => {
//     srcset.push(`${getSizedMediaUrl(media, supportedSize + 'x')} ${supportedSize}w`);
//   });
//   return srcset.join(',');
// }
function getSupportedSizes(media, desiredSizes) {
  let supportedSizes = [],
    mediaWidth = media['preview_image']['width'];
  desiredSizes.forEach((width) => {
    if (mediaWidth >= width) {
      supportedSizes.push(width);
    }
  });
  return supportedSizes;
}
function imageLoaded(image) {
  return new Promise((resolve) => {
    if (!image || image.tagName !== 'IMG' || image.complete) {
      resolve();
    } else {
      image.onload = () => resolve();
    }
  });
}

// js/helper/animation.js
var CustomAnimation = class {
  constructor(effect) {
    this._effect = effect;
    this._playState = 'idle';
    this._finished = Promise.resolve();
  }
  get finished() {
    return this._finished;
  }
  get animationEffects() {
    return this._effect instanceof CustomKeyframeEffect ? [this._effect] : this._effect.animationEffects;
  }
  cancel() {
    this.animationEffects.forEach((animationEffect) => animationEffect.cancel());
  }
  finish() {
    this.animationEffects.forEach((animationEffect) => animationEffect.finish());
  }
  play() {
    this._playState = 'running';
    this._effect.play();
    this._finished = this._effect.finished;
    this._finished.then(
      () => {
        this._playState = 'finished';
      },
      (rejection) => {
        this._playState = 'idle';
      }
    );
  }
};
var CustomKeyframeEffect = class {
  constructor(target, keyframes, options = {}) {
    if (!target) {
      return;
    }
    if ('Animation' in window) {
      this._animation = new Animation(new KeyframeEffect(target, keyframes, options));
    } else {
      options['fill'] = 'forwards';
      this._animation = target.animate(keyframes, options);
      this._animation.pause();
    }
    this._animation.addEventListener('finish', () => {
      target.style.opacity = keyframes.hasOwnProperty('opacity') ? keyframes['opacity'][keyframes['opacity'].length - 1] : null;
      target.style.visibility = keyframes.hasOwnProperty('visibility') ? keyframes['visibility'][keyframes['visibility'].length - 1] : null;
    });
  }
  get finished() {
    if (!this._animation) {
      return Promise.resolve();
    }
    return this._animation.finished ? this._animation.finished : new Promise((resolve) => (this._animation.onfinish = resolve));
  }
  play() {
    if (this._animation) {
      this._animation.startTime = null;
      this._animation.play();
    }
  }
  cancel() {
    if (this._animation) {
      this._animation.cancel();
    }
  }
  finish() {
    if (this._animation) {
      this._animation.finish();
    }
  }
};
var GroupEffect = class {
  constructor(childrenEffects) {
    this._childrenEffects = childrenEffects;
    this._finished = Promise.resolve();
  }
  get finished() {
    return this._finished;
  }
  get animationEffects() {
    return this._childrenEffects.flatMap((effect) => {
      return effect instanceof CustomKeyframeEffect ? effect : effect.animationEffects;
    });
  }
};
var ParallelEffect = class extends GroupEffect {
  play() {
    const promises = [];
    for (const effect of this._childrenEffects) {
      effect.play();
      promises.push(effect.finished);
    }
    this._finished = Promise.all(promises);
  }
};
var SequenceEffect = class extends GroupEffect {
  play() {
    this._finished = new Promise(async (resolve, reject) => {
      try {
        for (const effect of this._childrenEffects) {
          effect.play();
          await effect.finished;
        }
        resolve();
      } catch (exception) {
        reject();
      }
    });
  }
};

// js/custom-element/section/logo-list/logo-list.js
var LogoList = class extends CustomHTMLElement {
  async connectedCallback() {
    this.items = Array.from(this.querySelectorAll('.logo-list__item'));
    this.logoListScrollable = this.querySelector('.logo-list__list');
    if (this.items.length > 1) {
      this.addEventListener('prev-next:prev', this.previous.bind(this));
      this.addEventListener('prev-next:next', this.next.bind(this));
    }
    if (this.hasAttribute('reveal-on-scroll')) {
      this._setupVisibility();
    }
  }
  async _setupVisibility() {
    await this.untilVisible({ rootMargin: '50px 0px', threshold: 0 });
    const animation = new CustomAnimation(
      new ParallelEffect(
        this.items.map((item, index) => {
          return new CustomKeyframeEffect(
            item,
            {
              opacity: [0, 1],
              transform: [`translateY(${MediaFeatures.prefersReducedMotion() ? 0 : '30px'})`, 'translateY(0)'],
            },
            {
              duration: 300,
              delay: MediaFeatures.prefersReducedMotion() ? 0 : 100 * index,
              easing: 'ease',
            }
          );
        })
      )
    );
    this._hasSectionReloaded ? animation.finish() : animation.play();
  }
  previous(event) {
    const directionFlip = window.themeVariables.settings.direction === 'ltr' ? 1 : -1;
    event.target.nextElementSibling.removeAttribute('disabled');
    event.target.toggleAttribute('disabled', this.logoListScrollable.scrollLeft * directionFlip - (this.logoListScrollable.clientWidth + 24) <= 0);
    this.logoListScrollable.scrollBy({ left: -(this.logoListScrollable.clientWidth + 24) * directionFlip, behavior: 'smooth' });
  }
  next(event) {
    const directionFlip = window.themeVariables.settings.direction === 'ltr' ? 1 : -1;
    event.target.previousElementSibling.removeAttribute('disabled');
    event.target.toggleAttribute('disabled', this.logoListScrollable.scrollLeft * directionFlip + (this.logoListScrollable.clientWidth + 24) * 2 >= this.logoListScrollable.scrollWidth);
    this.logoListScrollable.scrollBy({ left: (this.logoListScrollable.clientWidth + 24) * directionFlip, behavior: 'smooth' });
  }
};
window.customElements.define('logo-list', LogoList);

// js/custom-element/section/blog/blog-post-navigation.js
var BlogPostNavigation = class extends HTMLElement {
  connectedCallback() {
    window.addEventListener('scroll', throttle(this._updateProgressBar.bind(this), 15));
  }
  get hasNextArticle() {
    return this.hasAttribute('has-next-article');
  }
  _updateProgressBar() {
    const stickyHeaderOffset = getStickyHeaderOffset(),
      marginCompensation = window.matchMedia(window.themeVariables.breakpoints.pocket).matches ? 40 : 80,
      articleNavBoundingBox = this.getBoundingClientRect(),
      articleMainPartBoundingBox = this.parentElement.getBoundingClientRect(),
      difference = articleMainPartBoundingBox.bottom - (articleNavBoundingBox.bottom - marginCompensation),
      progress = Math.max(-1 * (difference / (articleMainPartBoundingBox.height + marginCompensation) - 1), 0);
    this.classList.toggle('is-visible', articleMainPartBoundingBox.top < stickyHeaderOffset && articleMainPartBoundingBox.bottom > stickyHeaderOffset + this.clientHeight - marginCompensation);
    if (this.hasNextArticle) {
      if (progress > 0.8) {
        this.classList.add('article__nav--show-next');
      } else {
        this.classList.remove('article__nav--show-next');
      }
    }
    this.style.setProperty('--transform', `${progress}`);
  }
};
window.customElements.define('blog-post-navigation', BlogPostNavigation);

// js/custom-element/section/multi-column/multi-column.js
var MultiColumn = class extends CustomHTMLElement {
  connectedCallback() {
    if (!this.hasAttribute('stack')) {
      this.multiColumnInner = this.querySelector('.multi-column__inner');
      this.addEventListener('prev-next:prev', this.previous.bind(this));
      this.addEventListener('prev-next:next', this.next.bind(this));
      if (Shopify.designMode) {
        this.addEventListener('shopify:block:select', (event) => {
          event.target.scrollIntoView({ inline: 'center', block: 'nearest', behavior: event.detail.load ? 'auto' : 'smooth' });
        });
      }
    }
    if (this.hasAttribute('stagger-apparition')) {
      this._setupVisibility();
    }
  }
  async _setupVisibility() {
    await this.untilVisible({ threshold: Math.min(50 / this.clientHeight, 1) });
    const prefersReducedMotion = MediaFeatures.prefersReducedMotion();
    const animation = new CustomAnimation(
      new ParallelEffect(
        Array.from(this.querySelectorAll('.multi-column__item')).map((item, index) => {
          return new CustomKeyframeEffect(
            item,
            {
              opacity: [0, 1],
              transform: [`translateY(${MediaFeatures.prefersReducedMotion() ? 0 : window.innerWidth < 1e3 ? 35 : 60}px)`, 'translateY(0)'],
            },
            {
              duration: 600,
              delay: prefersReducedMotion ? 0 : 100 * index,
              easing: 'ease',
            }
          );
        })
      )
    );
    this._hasSectionReloaded ? animation.finish() : animation.play();
  }
  // Move to previous products (if any)
  previous(event) {
    const directionFlip = window.themeVariables.settings.direction === 'ltr' ? 1 : -1,
      columnGap = parseInt(getComputedStyle(this).getPropertyValue('--multi-column-column-gap'));
    event.target.nextElementSibling.removeAttribute('disabled');
    event.target.toggleAttribute('disabled', this.multiColumnInner.scrollLeft * directionFlip - (this.multiColumnInner.clientWidth + columnGap) <= 0);
    this.multiColumnInner.scrollBy({ left: -(this.multiColumnInner.clientWidth + columnGap) * directionFlip, behavior: 'smooth' });
  }
  // Move to next products (if any)
  next(event) {
    const directionFlip = window.themeVariables.settings.direction === 'ltr' ? 1 : -1,
      columnGap = parseInt(getComputedStyle(this).getPropertyValue('--multi-column-column-gap'));
    event.target.previousElementSibling.removeAttribute('disabled');
    event.target.toggleAttribute('disabled', this.multiColumnInner.scrollLeft * directionFlip + (this.multiColumnInner.clientWidth + columnGap) * 2 >= this.multiColumnInner.scrollWidth);
    this.multiColumnInner.scrollBy({ left: (this.multiColumnInner.clientWidth + columnGap) * directionFlip, behavior: 'smooth' });
  }
};
window.customElements.define('multi-column', MultiColumn);

// js/custom-element/section/blog/article-list.js
var ArticleList = class extends CustomHTMLElement {
  async connectedCallback() {
    this.articleItems = Array.from(this.querySelectorAll('.article-item'));
    this.pageDotsElements = Array.from(this.querySelectorAll('page-dots'));

    if (this.articleItems.length > 1) {
      this.addEventListener('page-dots:changed', (event) => this.select(event.detail.index, true));

      const scrollerElement = this.articleItems[0].closest('.scroller');
      this.intersectionObserver = new IntersectionObserver(this._onVisibilityChanged.bind(this), { root: scrollerElement, rootMargin: `${scrollerElement.clientHeight}px 0px`, threshold: 0.8 });
      this.articleItems.forEach((item) => this.intersectionObserver.observe(item));
    }

    if (this.staggerApparition) {
      await this.untilVisible({ threshold: this.clientHeight > 0 ? Math.min(50 / this.clientHeight, 1) : 0 });
      const animation = new CustomAnimation(
        new ParallelEffect(
          this.articleItems.map((item, index) => {
            return new CustomKeyframeEffect(
              item,
              {
                opacity: [0, 1],
                transform: [`translateY(${MediaFeatures.prefersReducedMotion() ? 0 : window.innerWidth < 1e3 ? 35 : 60}px)`, 'translateY(0)'],
              },
              {
                duration: 600,
                delay: MediaFeatures.prefersReducedMotion() ? 0 : 100 * index - Math.min(3 * index * index, 100 * index),
                easing: 'ease',
              }
            );
          })
        )
      );
      this._hasSectionReloaded ? animation.finish() : animation.play();
    }
  }
  get staggerApparition() {
    return this.hasAttribute('stagger-apparition');
  }
  select(index, shouldAnimate = true) {
    const clampIndex = Math.max(0, Math.min(index, this.articleItems.length));
    const selectedElement = this.articleItems[clampIndex];
    this._adjustNavigationForElement(selectedElement);

    if (shouldAnimate) {
      this.articleItems.forEach((item) => this.intersectionObserver.unobserve(item));
      setInterval(() => {
        this.articleItems.forEach((item) => this.intersectionObserver.observe(item));
      }, 800);
    }
    this.articleItems.forEach((item, loopIndex) => (item.selected = loopIndex === clampIndex));
    const direction = window.themeVariables.settings.direction === 'ltr' ? 1 : -1;
    selectedElement.closest('.scroller').scrollTo({ left: direction * (selectedElement.clientWidth * clampIndex), behavior: shouldAnimate ? 'smooth' : 'auto' });
  }
  _adjustNavigationForElement(selectedElement) {
    const index = this.articleItems.findIndex((item) => item.isSameNode(selectedElement));
    this.pageDotsElements.forEach((pageDot) => (pageDot.selectedIndex = index));
  }
  _onVisibilityChanged(entries) {
    for (let entry of entries) {
      if (entry.isIntersecting) {
        this._adjustNavigationForElement(entry.target);
        break;
      }
    }
  }
};
window.customElements.define('article-list', ArticleList);

// js/custom-element/section/blog/blog-post-header.js
var BlogPostHeader = class extends HTMLElement {
  async connectedCallback() {
    const image = this.querySelector('.article__image');
    if (MediaFeatures.prefersReducedMotion()) {
      image.removeAttribute('reveal');
    } else {
      await imageLoaded(image);
      image.animate({ opacity: [0, 1], transform: ['scale(1.1)', 'scale(1)'] }, { duration: 500, fill: 'forwards', easing: 'cubic-bezier(0.65, 0, 0.35, 1)' });
    }
  }
};
window.customElements.define('blog-post-header', BlogPostHeader);

// js/custom-element/section/search/predictive-search-input.js
var PredictiveSearchInput = class extends HTMLInputElement {
  connectedCallback() {
    this.addEventListener('click', () => (document.getElementById(this.getAttribute('aria-controls')).open = true));
  }
};
window.customElements.define('predictive-search-input', PredictiveSearchInput, { extends: 'input' });

// js/custom-element/ui/drawer.js
var DrawerContent = class extends OpenableElement {
  connectedCallback() {
    super.connectedCallback();
    if (this.hasAttribute('reverse-breakpoint')) {
      this.originalDirection = this.classList.contains('drawer--from-left') ? 'left' : 'right';
      const matchMedia2 = window.matchMedia(this.getAttribute('reverse-breakpoint'));
      matchMedia2.addListener(this._checkReverseOpeningDirection.bind(this));
      this._checkReverseOpeningDirection(matchMedia2);
    }
    this.delegate.on('click', '.drawer__overlay', () => (this.open = false));
  }
  attributeChangedCallback(name, oldValue, newValue) {
    super.attributeChangedCallback(name, oldValue, newValue);
    switch (name) {
      case 'open':
        document.documentElement.classList.toggle('lock-all', this.open);
    }
  }
  _checkReverseOpeningDirection(match) {
    this.classList.remove('drawer--from-left');
    if ((this.originalDirection === 'left' && !match.matches) || (this.originalDirection !== 'left' && match.matches)) {
      this.classList.add('drawer--from-left');
    }
  }
};
window.customElements.define('drawer-content', DrawerContent);

// js/custom-element/section/header/desktop-navigation.js
var DesktopNavigation = class extends CustomHTMLElement {
  connectedCallback() {
    this.openingTimeout = null;
    this.currentMegaMenu = null;
    this.delegate.on(
      'mouseenter',
      '.has-dropdown',
      (event, target) => {
        if (event.target === target && event.relatedTarget !== null) {
          this.openDropdown(target);
        }
      },
      true
    );
    this.delegate.on('click', '.header__linklist-link[aria-expanded], .nav-dropdown__link[aria-expanded]', (event, target) => {
      if (window.matchMedia('(hover: hover)').matches || target.getAttribute('aria-expanded') === 'true') {
        return;
      }
      event.preventDefault();
      this.openDropdown(target.parentElement);
    });
    this.delegate.on('shopify:block:select', (event) => this.openDropdown(event.target.parentElement));
    this.delegate.on('shopify:block:deselect', (event) => this.closeDropdown(event.target.parentElement));
  }
  openDropdown(parentElement) {
    const menuItem = parentElement.querySelector('[aria-controls]'),
      dropdown = parentElement.querySelector(`#${menuItem.getAttribute('aria-controls')}`);
    this.currentMegaMenu = dropdown?.classList?.contains('mega-menu') ? dropdown : null;
    let openingTimeout = setTimeout(() => {
      if (menuItem.getAttribute('aria-expanded') === 'true') {
        return;
      }
      menuItem.setAttribute('aria-expanded', 'true');
      dropdown.removeAttribute('hidden');
      if (dropdown.classList.contains('mega-menu') && !MediaFeatures.prefersReducedMotion()) {
        const items = Array.from(dropdown.querySelectorAll('.mega-menu__column, .mega-menu__image-push'));
        items.forEach((item) => {
          item.getAnimations().forEach((animation2) => animation2.cancel());
          item.style.opacity = 0;
        });
        const animation = new CustomAnimation(
          new ParallelEffect(
            items.map((item, index) => {
              return new CustomKeyframeEffect(
                item,
                {
                  opacity: [0, 1],
                  transform: ['translateY(20px)', 'translateY(0)'],
                },
                {
                  duration: 250,
                  delay: 100 + 60 * index,
                  easing: 'cubic-bezier(0.65, 0, 0.35, 1)',
                }
              );
            })
          )
        );
        animation.play();
      }
      const leaveListener = (event) => {
        if (event.relatedTarget !== null) {
          this.closeDropdown(parentElement);
          parentElement.removeEventListener('mouseleave', leaveListener);
        }
      };
      const leaveDocumentListener = () => {
        this.closeDropdown(parentElement);
        document.documentElement.removeEventListener('mouseleave', leaveDocumentListener);
      };
      parentElement.addEventListener('mouseleave', leaveListener);
      document.documentElement.addEventListener('mouseleave', leaveDocumentListener);
      openingTimeout = null;
      this.dispatchEvent(new CustomEvent('desktop-nav:dropdown:open', { bubbles: true }));
    }, 100);
    parentElement.addEventListener(
      'mouseleave',
      () => {
        if (openingTimeout) {
          clearTimeout(openingTimeout);
        }
      },
      { once: true }
    );
  }
  closeDropdown(parentElement) {
    const menuItem = parentElement.querySelector('[aria-controls]'),
      dropdown = parentElement.querySelector(`#${menuItem.getAttribute('aria-controls')}`);
    requestAnimationFrame(() => {
      dropdown.classList.add('is-closing');
      menuItem.setAttribute('aria-expanded', 'false');
      setTimeout(
        () => {
          dropdown.setAttribute('hidden', '');
          clearTimeout(this.openingTimeout);
          dropdown.classList.remove('is-closing');
        },
        dropdown.classList.contains('mega-menu') && this.currentMegaMenu !== dropdown ? 250 : 0
      );
      this.dispatchEvent(new CustomEvent('desktop-nav:dropdown:close', { bubbles: true }));
    });
  }
};
window.customElements.define('desktop-navigation', DesktopNavigation);

// js/custom-element/section/header/mobile-navigation.js
var MobileNavigation = class extends DrawerContent {
  get apparitionAnimation() {
    if (this._apparitionAnimation) {
      return this._apparitionAnimation;
    }
    if (!MediaFeatures.prefersReducedMotion()) {
      const navItems = Array.from(this.querySelectorAll('.mobile-nav__item[data-level="1"]')),
        effects = [];
      effects.push(
        new ParallelEffect(
          navItems.map((item, index) => {
            return new CustomKeyframeEffect(
              item,
              {
                opacity: [0, 1],
                transform: ['translateX(-40px)', 'translateX(0)'],
              },
              {
                duration: 300,
                delay: 300 + 120 * index - Math.min(2 * index * index, 120 * index),
                easing: 'cubic-bezier(0.25, 1, 0.5, 1)',
              }
            );
          })
        )
      );
      const bottomBar = this.querySelector('.drawer__footer');
      if (bottomBar) {
        effects.push(
          new CustomKeyframeEffect(
            bottomBar,
            {
              opacity: [0, 1],
              transform: ['translateY(100%)', 'translateY(0)'],
            },
            {
              duration: 300,
              delay: 500 + Math.max(125 * navItems.length - 25 * navItems.length, 25),
              easing: 'cubic-bezier(0.25, 1, 0.5, 1)',
            }
          )
        );
      }
      return (this._apparitionAnimation = new CustomAnimation(new ParallelEffect(effects)));
    }
  }
  attributeChangedCallback(name, oldValue, newValue) {
    super.attributeChangedCallback(name, oldValue, newValue);
    switch (name) {
      case 'open':
        if (this.open && this.apparitionAnimation) {
          Array.from(this.querySelectorAll('.mobile-nav__item[data-level="1"], .drawer__footer')).forEach((item) => (item.style.opacity = 0));
          this.apparitionAnimation.play();
        }
        triggerEvent(this, this.open ? 'mobile-nav:open' : 'mobile-nav:close');
    }
  }
};
window.customElements.define('mobile-navigation', MobileNavigation);

// js/custom-element/section/header/store-header.js
var StoreHeader = class extends CustomHTMLElement {
  connectedCallback() {
    if (window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver(this._updateCustomProperties.bind(this));
      this.resizeObserver.observe(this);
      this.resizeObserver.observe(this.querySelector('.header__wrapper'));
    }
    if (this.isTransparent) {
      this.isTransparencyDetectionLocked = false;
      this.delegate.on('desktop-nav:dropdown:open', () => (this.lockTransparency = true));
      this.delegate.on('desktop-nav:dropdown:close', () => (this.lockTransparency = false));
      this.rootDelegate.on('mobile-nav:open', () => (this.lockTransparency = true));
      this.rootDelegate.on('mobile-nav:close', () => (this.lockTransparency = false));
      this.delegate.on('mouseenter', this._checkTransparentHeader.bind(this), true);
      this.delegate.on('mouseleave', this._checkTransparentHeader.bind(this));
      if (this.isSticky) {
        this._checkTransparentHeader();
        this._onWindowScrollListener = throttle(this._checkTransparentHeader.bind(this), 100);
        window.addEventListener('scroll', this._onWindowScrollListener);
      }
    }
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    if (window.ResizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.isTransparent && this.isSticky) {
      window.removeEventListener('scroll', this._onWindowScrollListener);
    }
  }
  get isSticky() {
    return this.hasAttribute('sticky');
  }
  get isTransparent() {
    return this.hasAttribute('transparent');
  }
  get transparentHeaderThreshold() {
    return 25;
  }
  set lockTransparency(value) {
    this.isTransparencyDetectionLocked = value;
    this._checkTransparentHeader();
  }
  /**
   * This method allows to keep in sync various CSS variables that are used to size various elements
   */
  _updateCustomProperties(entries) {
    entries.forEach((entry) => {
      if (entry.target === this) {
        const height = entry.borderBoxSize ? (entry.borderBoxSize.length > 0 ? entry.borderBoxSize[0].blockSize : entry.borderBoxSize.blockSize) : entry.target.clientHeight;
        document.documentElement.style.setProperty('--header-height', `${height}px`);
      }
      if (entry.target.classList.contains('header__wrapper')) {
        const heightWithoutNav = entry.borderBoxSize ? (entry.borderBoxSize.length > 0 ? entry.borderBoxSize[0].blockSize : entry.borderBoxSize.blockSize) : entry.target.clientHeight;
        document.documentElement.style.setProperty('--header-height-without-bottom-nav', `${heightWithoutNav}px`);
      }
    });
  }
  _checkTransparentHeader(event) {
    if (this.isTransparencyDetectionLocked || window.scrollY > this.transparentHeaderThreshold || (event && event.type === 'mouseenter')) {
      this.classList.remove('header--transparent');
    } else {
      this.classList.add('header--transparent');
    }
  }
};
window.customElements.define('store-header', StoreHeader);

// js/custom-element/section/product/gift-card-recipient.js
var GiftCardRecipient = class extends HTMLElement {
  connectedCallback() {
    const properties = Array.from(this.querySelectorAll('[name*="properties"]')),
      checkboxPropertyName = 'properties[__shopify_send_gift_card_to_recipient]';
    this.recipientCheckbox = properties.find((input) => input.name === checkboxPropertyName);
    this.recipientOtherProperties = properties.filter((input) => input.name !== checkboxPropertyName);
    this.recipientFieldsContainer = this.querySelector('.gift-card-recipient__fields');
    this.recipientCheckbox?.addEventListener('change', this._synchronizeProperties.bind(this));
    this.offsetProperty = this.querySelector('[name="properties[__shopify_offset]"]');
    if (this.offsetProperty) {
      this.offsetProperty.value = /* @__PURE__ */ new Date().getTimezoneOffset().toString();
    }
    this.recipientSendOnProperty = this.querySelector('[name="properties[Send on]"]');
    const minDate = /* @__PURE__ */ new Date();
    const maxDate = /* @__PURE__ */ new Date();
    maxDate.setDate(minDate.getDate() + 90);
    this.recipientSendOnProperty?.setAttribute('min', this._formatDate(minDate));
    this.recipientSendOnProperty?.setAttribute('max', this._formatDate(maxDate));
    this._synchronizeProperties();
  }
  _synchronizeProperties() {
    this.recipientOtherProperties.forEach((property) => (property.disabled = !this.recipientCheckbox.checked));
    this.recipientFieldsContainer.classList.toggle('js:hidden', !this.recipientCheckbox.checked);
  }
  _formatDate(date) {
    const offset = date.getTimezoneOffset();
    const offsetDate = new Date(date.getTime() - offset * 60 * 1e3);
    return offsetDate.toISOString().split('T')[0];
  }
};
if (!window.customElements.get('gift-card-recipient')) {
  window.customElements.define('gift-card-recipient', GiftCardRecipient);
}

// js/custom-element/section/product/product-rerender.js
var _abortController, _ProductRerender_instances, onRerender_fn;
var ProductRerender = class extends HTMLElement {
  constructor() {
    super(...arguments);
    __privateAdd(this, _ProductRerender_instances);
    __privateAdd(this, _abortController);
  }
  connectedCallback() {
    __privateSet(this, _abortController, new AbortController());
    if (!this.id || !this.hasAttribute('observe-form')) {
      console.warn('The <product-rerender> requires an ID to identify the element to re-render, and an "observe-form" attribute referencing to the form to monitor.');
    }
    document.forms[this.getAttribute('observe-form')].addEventListener('product:rerender', __privateMethod(this, _ProductRerender_instances, onRerender_fn).bind(this), { signal: __privateGet(this, _abortController).signal });
  }
  disconnectedCallback() {
    __privateGet(this, _abortController).abort();
  }
};
_abortController = new WeakMap();
_ProductRerender_instances = new WeakSet();
onRerender_fn = function (event) {
  const matchingElement = deepQuerySelector(event.detail.htmlFragment, `#${this.id}`);
  if (!matchingElement) {
    return;
  }
  const focusedElement = document.activeElement;
  if (!this.hasAttribute('allow-partial-rerender') || event.detail.productChange) {
    this.replaceWith(matchingElement);
  } else {
    const blockTypes = ['meta', 'payment-terms', 'variant-picker', 'inventory', 'buy-buttons', 'pickup-availability', 'liquid'];
    blockTypes.forEach((blockType) => {
      this.querySelectorAll(`[data-block-type="${blockType}"]`).forEach((element) => {
        const matchingBlock = matchingElement.querySelector(`[data-block-type="${blockType}"][data-block-id="${element.getAttribute('data-block-id')}"]`);
        if (matchingBlock) {
          if (blockType === 'buy-buttons') {
            element.querySelector('product-payment-container').replaceWith(matchingBlock.querySelector('product-payment-container'));
          } else {
            element.replaceWith(matchingBlock);
          }
        }
      });
    });
  }
  if (focusedElement.id) {
    const element = document.getElementById(focusedElement.id);
    if (this.contains(element)) {
      element.focus();
    }
  }
};
if (!window.customElements.get('product-rerender')) {
  window.customElements.define('product-rerender', ProductRerender);
}

// js/custom-element/section/product/image-zoom.js
var PhotoSwipeUi = class {
  constructor(pswp) {
    this.photoSwipeInstance = pswp;
    this.delegate = new main_default(this.photoSwipeInstance.scrollWrap);
    this.maxSpreadZoom = window.themeVariables.settings.mobileZoomFactor || 2;
    this.pswpUi = this.photoSwipeInstance.scrollWrap.querySelector('.pswp__ui');
    this.delegate.on('click', '[data-action="pswp-close"]', this._close.bind(this));
    this.delegate.on('click', '[data-action="pswp-prev"]', this._goToPrev.bind(this));
    this.delegate.on('click', '[data-action="pswp-next"]', this._goToNext.bind(this));
    this.delegate.on('click', '[data-action="pswp-move-to"]', this._moveTo.bind(this));
    this.photoSwipeInstance.listen('close', this._onPswpClosed.bind(this));
    this.photoSwipeInstance.listen('doubleTap', this._onPswpDoubleTap.bind(this));
    this.photoSwipeInstance.listen('beforeChange', this._onPswpBeforeChange.bind(this));
    this.photoSwipeInstance.listen('initialZoomInEnd', this._onPswpInitialZoomInEnd.bind(this));
    this.photoSwipeInstance.listen('initialZoomOut', this._onPswpInitialZoomOut.bind(this));
    this.photoSwipeInstance.listen('parseVerticalMargin', this._onPswpParseVerticalMargin.bind(this));
    this.delegate.on('pswpTap', '.pswp__img', this._onPswpTap.bind(this));
  }
  init() {
    const prevNextButtons = this.pswpUi.querySelector('.pswp__prev-next-buttons'),
      dotsNavWrapper = this.pswpUi.querySelector('.pswp__dots-nav-wrapper');
    if (this.photoSwipeInstance.items.length <= 1) {
      prevNextButtons.style.display = 'none';
      dotsNavWrapper.style.display = 'none';
      return;
    }
    prevNextButtons.style.display = '';
    dotsNavWrapper.style.display = '';
    let dotsNavHtml = '';
    this.photoSwipeInstance.items.forEach((item, index) => {
      dotsNavHtml += `
        <button class="dots-nav__item tap-area" ${index === 0 ? 'aria-current="true"' : ''} data-action="pswp-move-to">
          <span class="visually-hidden">Go to slide ${index}</span>
        </button>
      `;
    });
    dotsNavWrapper.querySelector('.pswp__dots-nav-wrapper .dots-nav').innerHTML = dotsNavHtml;
  }
  _close() {
    this.photoSwipeInstance.close();
  }
  _goToPrev() {
    this.photoSwipeInstance.prev();
  }
  _goToNext() {
    this.photoSwipeInstance.next();
  }
  _moveTo(event, target) {
    this.photoSwipeInstance.goTo([...target.parentNode.children].indexOf(target));
  }
  _onPswpClosed() {
    this.delegate.off('pswpTap');
  }
  _onPswpDoubleTap(point) {
    const initialZoomLevel = this.photoSwipeInstance.currItem.initialZoomLevel;
    if (this.photoSwipeInstance.getZoomLevel() !== initialZoomLevel) {
      this.photoSwipeInstance.zoomTo(initialZoomLevel, point, 333);
    } else {
      this.photoSwipeInstance.zoomTo(initialZoomLevel < 0.7 ? 1 : this.maxSpreadZoom, point, 333);
    }
  }
  _onPswpTap(event) {
    if (event.detail.pointerType === 'mouse') {
      this.photoSwipeInstance.toggleDesktopZoom(event.detail.releasePoint);
    }
  }
  _onPswpBeforeChange() {
    if (this.photoSwipeInstance.items.length <= 1) {
      return;
    }
    const activeDot = this.photoSwipeInstance.scrollWrap.querySelector(`.dots-nav__item:nth-child(${this.photoSwipeInstance.getCurrentIndex() + 1})`);
    activeDot.setAttribute('aria-current', 'true');
    getSiblings(activeDot).forEach((item) => item.removeAttribute('aria-current'));
  }
  _onPswpInitialZoomInEnd() {
    this.pswpUi?.classList.remove('pswp__ui--hidden');
  }
  _onPswpInitialZoomOut() {
    this.pswpUi?.classList.add('pswp__ui--hidden');
  }
  _onPswpParseVerticalMargin(item) {
    item.vGap.bottom = this.photoSwipeInstance.items.length <= 1 || window.matchMedia(window.themeVariables.breakpoints.lapAndUp).matches ? 0 : 60;
  }
};
var ProductImageZoom = class extends OpenableElement {
  connectedCallback() {
    super.connectedCallback();
    this.mediaElement = this.closest('.product__media');
    this.maxSpreadZoom = window.themeVariables.settings.mobileZoomFactor || 2;
    LibraryLoader.load('photoswipe');
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    this.photoSwipeInstance?.destroy();
  }
  async attributeChangedCallback(name, oldValue, newValue) {
    super.attributeChangedCallback(name, oldValue, newValue);
    switch (name) {
      case 'open':
        if (this.open) {
          await LibraryLoader.load('photoswipe');
          this._openPhotoSwipe();
        }
    }
  }
  async _openPhotoSwipe() {
    const items = await this._buildItems();
    this.photoSwipeInstance = new window.ThemePhotoSwipe(this, PhotoSwipeUi, items, {
      index: items.findIndex((item) => item.selected),
      maxSpreadZoom: this.maxSpreadZoom,
      loop: false,
      allowPanToNext: false,
      closeOnScroll: false,
      closeOnVerticalDrag: MediaFeatures.supportsHover(),
      showHideOpacity: true,
      arrowKeys: true,
      history: false,
      getThumbBoundsFn: () => {
        const thumbnail = this.mediaElement.querySelector('.product__media-item.is-selected'),
          pageYScroll = window.pageYOffset || document.documentElement.scrollTop,
          rect = thumbnail.getBoundingClientRect();
        return { x: rect.left, y: rect.top + pageYScroll, w: rect.width };
      },
      getDoubleTapZoom: (isMouseClick, item) => {
        if (isMouseClick) {
          return item.w > item.h ? 1.6 : 1;
        } else {
          return item.initialZoomLevel < 0.7 ? 1 : 1.33;
        }
      },
    });
    let lastWidth = null;
    this.photoSwipeInstance.updateSize = new Proxy(this.photoSwipeInstance.updateSize, {
      apply: (target, thisArg, argArray) => {
        if (lastWidth !== window.innerWidth) {
          target(arguments);
          lastWidth = window.innerWidth;
        }
      },
    });
    this.photoSwipeInstance.listen('close', () => {
      this.open = false;
    });
    this.photoSwipeInstance.init();
  }
  async _buildItems() {
    const activeImages = Array.from(this.mediaElement.querySelectorAll('.product__media-item[data-media-type="image"]:not(.is-filtered)')),
      product = await ProductLoader.load(this.getAttribute('product-handle'));
    return Promise.resolve(
      activeImages.map((item) => {
        const matchedMedia = product['media'].find((media) => media.id === parseInt(item.getAttribute('data-media-id'))),
          supportedSizes = getSupportedSizes(matchedMedia, [200, 300, 400, 500, 600, 700, 800, 1e3, 1200, 1400, 1600, 1800, 2e3, 2200, 2400, 2600, 2800, 3e3]),
          desiredWidth = Math.min(supportedSizes[supportedSizes.length - 1], window.innerWidth);
        return {
          selected: item.classList.contains('is-selected'),
          src: getSizedMediaUrl(matchedMedia, `${Math.ceil(Math.min(desiredWidth * window.devicePixelRatio * this.maxSpreadZoom, 3e3))}x`),
          msrc: item.firstElementChild.currentSrc,
          originalMedia: matchedMedia,
          w: desiredWidth,
          h: parseInt(desiredWidth / matchedMedia['aspect_ratio']),
        };
      })
    );
  }
};
window.customElements.define('product-image-zoom', ProductImageZoom);

// js/custom-element/section/product/inventory.js
var ProductInventory = class extends HTMLElement {
  connectedCallback() {
    const scriptTag = this.querySelector('script');
    if (!scriptTag) {
      return;
    }
    this.inventories = JSON.parse(scriptTag.innerHTML);
    document.getElementById(this.getAttribute('form-id'))?.addEventListener('variant:changed', this._onVariantChanged.bind(this));
  }
  _onVariantChanged(event) {
    this.querySelector('span')?.remove();
    if (event.detail.variant && this.inventories[event.detail.variant['id']] !== '') {
      this.hidden = false;
      this.insertAdjacentHTML('afterbegin', this.inventories[event.detail.variant['id']]);
    } else {
      this.hidden = true;
    }
  }
};
window.customElements.define('product-inventory', ProductInventory);

// js/custom-element/section/product/payment-container.js
// var PaymentContainer = class extends HTMLElement {
//   connectedCallback() {
//     document.getElementById(this.getAttribute('form-id'))?.addEventListener('variant:changed', this._onVariantChanged.bind(this));
//     if (Shopify.designMode && Shopify.PaymentButton) {
//       Shopify.PaymentButton.init();
//     }
//   }
//   _onVariantChanged(event) {
//     this._updateAddToCartButton(event.detail.variant);
//     this._updateDynamicCheckoutButton(event.detail.variant);
//   }
//   _updateAddToCartButton(variant) {
//     let addToCartButtonElement = this.querySelector('[data-product-add-to-cart-button]');
//     if (!addToCartButtonElement) {
//       return;
//     }
//     let addToCartButtonText = '';
//     addToCartButtonElement.classList.remove('button--primary', 'button--secondary', 'button--ternary');
//     if (!variant) {
//       addToCartButtonElement.setAttribute('disabled', 'disabled');
//       addToCartButtonElement.classList.add('button--ternary');
//       addToCartButtonText = window.themeVariables.strings.productFormUnavailable;
//     } else {
//       if (variant['available']) {
//         addToCartButtonElement.removeAttribute('disabled');
//         addToCartButtonElement.classList.add(addToCartButtonElement.hasAttribute('data-use-primary') ? 'button--primary' : 'button--secondary');
//         addToCartButtonElement.setAttribute('data-button-content', 'disabled');
//         addToCartButtonText = addToCartButtonElement.getAttribute('data-button-content');
//       } else {
//         addToCartButtonElement.setAttribute('disabled', 'disabled');
//         addToCartButtonElement.classList.add('button--ternary');
//         addToCartButtonText = window.themeVariables.strings.productFormSoldOut;
//       }
//     }
//     if (addToCartButtonElement.getAttribute('is') === 'loader-button') {
//       addToCartButtonElement.firstElementChild.innerHTML = addToCartButtonText;
//     } else {
//       addToCartButtonElement.innerHTML = addToCartButtonText;
//     }
//   }
//   _updateDynamicCheckoutButton(variant) {
//     let paymentButtonElement = this.querySelector('.shopify-payment-button');
//     if (!paymentButtonElement) {
//       return;
//     }
//     paymentButtonElement.style.display = !variant || !variant['available'] ? 'none' : 'block';
//   }
// };
// window.customElements.define('product-payment-container', PaymentContainer);

var PaymentContainer = class extends HTMLElement {
  connectedCallback() {
    this._hasUnselectedRequiredOptions = false;
    const formElement = document.getElementById(this.getAttribute('form-id'));
    formElement?.addEventListener('variant:changed', this._onVariantChanged.bind(this));
    formElement?.addEventListener('variant:validation-changed', this._onValidationChanged.bind(this));
    formElement?.addEventListener('submit', this._onFormSubmit.bind(this));
    if (Shopify.designMode && Shopify.PaymentButton) {
      Shopify.PaymentButton.init();
    }
  }

  _onFormSubmit(event) {
    // Check validation state directly from product-variants at submit time
    const formId = this.getAttribute('form-id');
    const productVariants = document.querySelector(`product-variants[form-id="${formId}"]`);

    // Check if there are unselected required options
    const hasUnselected = productVariants && typeof productVariants._hasUnselectedRequiredOptions === 'function'
      ? productVariants._hasUnselectedRequiredOptions()
      : this._hasUnselectedRequiredOptions;

    if (!hasUnselected) return;

    // Prevent form submission
    event.preventDefault();
    event.stopPropagation();

    // Scroll to first unselected option
    if (productVariants && typeof productVariants.scrollToFirstUnselectedRequired === 'function') {
      productVariants.scrollToFirstUnselectedRequired();
    }
  }

  _onVariantChanged(event) {
    this._updateAddToCartButton(event.detail.variant);
    this._updateDynamicCheckoutButton(event.detail.variant);
  }

  _onValidationChanged(event) {
    this._hasUnselectedRequiredOptions = event.detail.hasUnselectedRequiredOptions;
    // Re-update the button with the current variant and new validation state
    const formElement = document.getElementById(this.getAttribute('form-id'));
    if (formElement) {
      const variantId = formElement.querySelector('[name="id"]')?.value;
      if (variantId) {
        // Trigger a button update with the current variant state
        this._updateAddToCartButton(this._getCurrentVariant());
      }
    }
  }

  _getCurrentVariant() {
    // Get the current variant from the form's hidden input or from a product-variants element
    const formId = this.getAttribute('form-id');
    const formElement = document.getElementById(formId);
    const variantId = formElement?.querySelector('[name="id"]')?.value;
    if (!variantId) return null;

    // Find product-variants by form-id attribute (it's outside the form, not inside)
    const productVariants = document.querySelector(`product-variants[form-id="${formId}"]`);
    if (productVariants && productVariants.selectedVariant) {
      return productVariants.selectedVariant;
    }

    return null;
  }

  _updateAddToCartButton(variant) {
    let addToCartButtonElement = this.querySelector('[data-product-add-to-cart-button]');
    if (!addToCartButtonElement) {
      return;
    }
    let addToCartButtonText = '';
    addToCartButtonElement.classList.remove('button--primary', 'button--secondary', 'button--ternary');

    // Don't disable button for unselected required options - validate on submit instead
    if (!variant) {
      addToCartButtonElement.setAttribute('disabled', 'disabled');
      addToCartButtonElement.classList.add('button--ternary');
      addToCartButtonText = window.themeVariables.strings.productFormUnavailable;
    } else {
      if (variant['available']) {
        addToCartButtonElement.removeAttribute('disabled');
        addToCartButtonElement.classList.add(addToCartButtonElement.hasAttribute('data-use-primary') ? 'button--primary' : 'button--secondary');
        addToCartButtonText = this._getButtonContentForVariant(variant);
        addToCartButtonElement.setAttribute('data-button-content', addToCartButtonText);
      } else {
        addToCartButtonElement.setAttribute('disabled', 'disabled');
        addToCartButtonElement.classList.add('button--ternary');
        addToCartButtonText = window.themeVariables.strings.productFormSoldOut;
      }
    }
    if (addToCartButtonElement.getAttribute('is') === 'loader-button') {
      addToCartButtonElement.firstElementChild.innerHTML = addToCartButtonText;
    } else {
      addToCartButtonElement.innerHTML = addToCartButtonText;
    }
  }

  _getButtonContentForVariant(variant) {
    let variantStatus = variantMetaData[variant.id] ? variantMetaData[variant.id].status : null;
    if (variantStatus === 'Pre-Order' || variantStatus === 'Always Pre-Order') {
      return window.themeVariables.strings.productFormPreOrder;
    }
    return window.themeVariables.strings.productFormAddToCart;
  }

  _updateDynamicCheckoutButton(variant) {
    let paymentButtonElement = this.querySelector('.shopify-payment-button');
    if (!paymentButtonElement) {
      return;
    }
    let variantStatus = variantMetaData[variant.id] ? variantMetaData[variant.id].status : null;
    if (!variant || !variant['available'] || variantStatus === 'Pre-Order' || variantStatus === 'Always Pre-Order') {
      paymentButtonElement.style.display = 'none';
    } else {
      paymentButtonElement.style.display = 'block';
    }
  }
};
window.customElements.define('product-payment-container', PaymentContainer);

// js/custom-element/section/product/payment-terms.js
var PaymentTerms = class extends CustomHTMLElement {
  connectedCallback() {
    document.getElementById(this.getAttribute('form-id'))?.addEventListener('variant:changed', this._onVariantChanged.bind(this));
  }
  _onVariantChanged(event) {
    const variant = event.detail.variant;
    if (variant) {
      const idElement = this.querySelector('[name="id"]');
      idElement.value = variant['id'];
      idElement.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }
};
window.customElements.define('product-payment-terms', PaymentTerms);

// js/helper/currency.js
function formatMoney(cents, format = '') {
  if (typeof cents === 'string') {
    cents = cents.replace('.', '');
  }
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/,
    formatString = format || window.themeVariables.settings.moneyFormat;
  function defaultTo(value2, defaultValue) {
    return value2 == null || value2 !== value2 ? defaultValue : value2;
  }
  function formatWithDelimiters(number, precision, thousands, decimal) {
    precision = defaultTo(precision, 2);
    thousands = defaultTo(thousands, ',');
    decimal = defaultTo(decimal, '.');
    if (isNaN(number) || number == null) {
      return 0;
    }
    number = (number / 100).toFixed(precision);
    let parts = number.split('.'),
      dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands),
      centsAmount = parts[1] ? decimal + parts[1] : '';
    return dollarsAmount + centsAmount;
  }
  let value = '';
  switch (formatString.match(placeholderRegex)[1]) {
    case 'amount':
      value = formatWithDelimiters(cents, 2);
      break;
    case 'amount_no_decimals':
      value = formatWithDelimiters(cents, 0);
      break;
    case 'amount_with_space_separator':
      value = formatWithDelimiters(cents, 2, ' ', '.');
      break;
    case 'amount_with_comma_separator':
      value = formatWithDelimiters(cents, 2, '.', ',');
      break;
    case 'amount_with_apostrophe_separator':
      value = formatWithDelimiters(cents, 2, "'", '.');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = formatWithDelimiters(cents, 0, '.', ',');
      break;
    case 'amount_no_decimals_with_space_separator':
      value = formatWithDelimiters(cents, 0, ' ');
      break;
    case 'amount_no_decimals_with_apostrophe_separator':
      value = formatWithDelimiters(cents, 0, "'");
      break;
  }
  if (formatString.indexOf('with_comma_separator') !== -1) {
    return formatString.replace(placeholderRegex, value);
  } else {
    return formatString.replace(placeholderRegex, value);
  }
}

// js/custom-element/section/product/product-meta.js
var ProductMeta = class extends HTMLElement {
  connectedCallback() {
    document.getElementById(this.getAttribute('form-id'))?.addEventListener('variant:changed', this._onVariantChanged.bind(this));
  }
  get priceClass() {
    return this.getAttribute('price-class') || '';
  }
  get unitPriceClass() {
    return this.getAttribute('unit-price-class') || '';
  }
  _onVariantChanged(event) {
    this._updateLabels(event.detail.variant);
    this._updatePrices(event.detail.variant);
    this._updateSubscriberPrice(event.detail.variant);
    this._updateSku(event.detail.variant);
  }
  _updateLabels(variant) {
    let productLabelList = this.querySelector('[data-product-label-list]');
    if (!productLabelList) {
      return;
    }
    if (!variant) {
      productLabelList.innerHTML = '';
    } else {
      productLabelList.innerHTML = '';
      if (!variant['available']) {
        productLabelList.innerHTML = `<span class="price--discount">${window.themeVariables.strings.collectionSoldOut}</span>`;
      } else if (variant['compare_at_price'] > variant['price']) {
        let savings = '';
        if (window.themeVariables.settings.discountMode === 'percentage') {
          savings = `${Math.round(((variant['compare_at_price'] - variant['price']) * 100) / variant['compare_at_price'])}%`;
        } else {
          savings = formatMoney(variant['compare_at_price'] - variant['price']);
        }
        productLabelList.innerHTML = `<span class="price--discount">${window.themeVariables.strings.collectionDiscount.replace('@savings@', savings)}</span>`;
      }
    }
  }
  _updatePrices(variant) {
    let productPrices = this.querySelector('[data-product-price-list]'),
      currencyFormat = window.themeVariables.settings.currencyCodeEnabled ? window.themeVariables.settings.moneyWithCurrencyFormat : window.themeVariables.settings.moneyFormat;
    if (!productPrices) {
      return;
    }
    if (!variant) {
      productPrices.style.display = 'none';
    } else {
      productPrices.innerHTML = '';
      if (variant['compare_at_price'] > variant['price']) {
        productPrices.innerHTML += `<span class="price price--compare"><span class="visually-hidden">${window.themeVariables.strings.productRegularPrice}</span>${formatMoney(variant['compare_at_price'], currencyFormat)}</span>`;
        productPrices.innerHTML += `<span class="price price--highlight ${this.priceClass}"><span class="visually-hidden">${window.themeVariables.strings.productSalePrice}</span>${formatMoney(variant['price'], currencyFormat)}</span>`;
      } else {
        productPrices.innerHTML += `<span class="price ${this.priceClass}"><span class="visually-hidden">${window.themeVariables.strings.productSalePrice}</span>${formatMoney(variant['price'], currencyFormat)}</span>`;
      }
      if (variant['unit_price_measurement']) {
        let referenceValue = '';
        if (variant['unit_price_measurement']['reference_value'] !== 1) {
          referenceValue = `<span class="unit-price-measurement__reference-value">${variant['unit_price_measurement']['reference_value']}</span>`;
        }
        productPrices.innerHTML += `
          <div class="price text--subdued ${this.unitPriceClass}">
            <div class="unit-price-measurement">
              <span class="unit-price-measurement__price">${formatMoney(variant['unit_price'])}</span>
              <span class="unit-price-measurement__separator">/</span>
              ${referenceValue}
              <span class="unit-price-measurement__reference-unit">${variant['unit_price_measurement']['reference_unit']}</span>
            </div>
          </div>
        `;
      }
      productPrices.style.display = '';
    }
  }
  _updateSubscriberPrice(variant) {
    let subscriberPriceEl = this.querySelector('[data-subscriber-price]');
    if (!subscriberPriceEl) {
      return;
    }
    if (!variant) {
      subscriberPriceEl.style.display = 'none';
    } else {
      let discountPercent = parseInt(subscriberPriceEl.getAttribute('data-discount-percent')) || 10;
      let messageTemplate = subscriberPriceEl.getAttribute('data-message-template') || 'Or [[price]] for newsletter subscribers';
      let currencyFormat = window.themeVariables.settings.currencyCodeEnabled ? window.themeVariables.settings.moneyWithCurrencyFormat : window.themeVariables.settings.moneyFormat;
      let discountedPrice = Math.round(variant['price'] * (100 - discountPercent) / 100);
      let formattedPrice = formatMoney(discountedPrice, currencyFormat);
      subscriberPriceEl.innerHTML = messageTemplate.replace('[[price]]', formattedPrice);
      subscriberPriceEl.style.display = '';
    }
  }
  _updateSku(variant) {
    let productSku = this.querySelector('[data-product-sku-container]');
    if (!productSku) {
      return;
    }
    let productSkuNumber = productSku.querySelector('[data-product-sku-number]');
    if (!variant || !variant['sku']) {
      productSku.style.display = 'none';
    } else {
      productSkuNumber.innerHTML = variant['sku'];
      productSku.style.display = '';
    }
  }
};
window.customElements.define('product-meta', ProductMeta);

// js/custom-element/section/product-list/quick-buy-drawer.js
var QuickBuyDrawer = class extends DrawerContent {
  connectedCallback() {
    super.connectedCallback();
    this.delegate.on('variant:changed', this._onVariantChanged.bind(this));
  }
  async _load() {
    await super._load();
    this.imageElement = this.querySelector('.quick-buy-product__image');
    if (window.Shopify && window.Shopify.PaymentButton) {
      window.Shopify.PaymentButton.init();
    }
  }
  _onVariantChanged(event) {
    const variant = event.detail.variant;
    if (variant) {
      Array.from(this.querySelectorAll(`[href*="/products"]`)).forEach((link) => {
        const url = new URL(link.href);
        url.searchParams.set('variant', variant['id']);
        link.setAttribute('href', url.toString());
      });
    }
    if (!this.imageElement || !variant || !variant['featured_media']) {
      return;
    }
    const featuredMedia = variant['featured_media'];
    if (featuredMedia['alt']) {
      this.imageElement.setAttribute('alt', featuredMedia['alt']);
    }
    this.imageElement.setAttribute('width', featuredMedia['preview_image']['width']);
    this.imageElement.setAttribute('height', featuredMedia['preview_image']['height']);
    this.imageElement.setAttribute('src', getSizedMediaUrl(featuredMedia, '342x'));
    this.imageElement.setAttribute('srcset', getMediaSrcset(featuredMedia, [114, 228, 342]));
  }
};
window.customElements.define('quick-buy-drawer', QuickBuyDrawer);

// js/custom-element/section/product-list/quick-buy-popover.js
var QuickBuyPopover = class extends PopoverContent {
  connectedCallback() {
    super.connectedCallback();
    this.delegate.on('variant:changed', this._onVariantChanged.bind(this));
    this.delegate.on('variant:added', () => (this.open = false));
  }
  async _load() {
    await super._load();
    this.imageElement = this.querySelector('.quick-buy-product__image');
  }
  _onVariantChanged(event) {
    const variant = event.detail.variant;
    if (variant) {
      Array.from(this.querySelectorAll(`[href*="/products"]`)).forEach((link) => {
        const url = new URL(link.href);
        url.searchParams.set('variant', variant['id']);
        link.setAttribute('href', url.toString());
      });
    }
    if (!this.imageElement || !variant || !variant['featured_media']) {
      return;
    }
    const featuredMedia = variant['featured_media'];
    if (featuredMedia['alt']) {
      this.imageElement.setAttribute('alt', featuredMedia['alt']);
    }
    this.imageElement.setAttribute('width', featuredMedia['preview_image']['width']);
    this.imageElement.setAttribute('height', featuredMedia['preview_image']['height']);
    this.imageElement.setAttribute('src', getSizedMediaUrl(featuredMedia, '720px'));
    this.imageElement.setAttribute('srcset', getMediaSrcset(featuredMedia, [65, 130, 195, 720, 1200]));
  }
};
window.customElements.define('quick-buy-popover', QuickBuyPopover);

// js/custom-element/section/product/store-pickup.js
var StorePickup = class extends HTMLElement {
  connectedCallback() {
    document.getElementById(this.getAttribute('form-id'))?.addEventListener('variant:changed', this._onVariantChanged.bind(this));
  }
  _onVariantChanged(event) {
    if (!event.detail.variant) {
      this.innerHTML = '';
    } else {
      this._renderForVariant(event.detail.variant['id']);
    }
  }
  async _renderForVariant(id) {
    const response = await fetch(`${window.themeVariables.routes.rootUrlWithoutSlash}/variants/${id}?section_id=store-availability`),
      div = document.createElement('div');
    div.innerHTML = await response.text();
    this.innerHTML = div.firstElementChild.innerHTML.trim();
  }
};
window.customElements.define('store-pickup', StorePickup);

// js/custom-element/section/cart/cart-count.js
var CartCount = class extends CustomHTMLElement {
  connectedCallback() {
    this.rootDelegate.on('cart:updated', (event) => (this.innerText = event.detail.cart['item_count']));
    this.rootDelegate.on('cart:refresh', this._updateCartCount.bind(this));
  }
  _updateCartCount(event) {
    if (event?.detail?.cart) {
      this.innerText = event.detail.cart['item_count'];
    } else {
      fetch(Shopify.routes.root + 'cart.js').then((response) => {
        response.json().then((responseAsJson) => {
          this.innerText = responseAsJson['item_count'];
        });
      });
    }
  }
};
window.customElements.define('cart-count', CartCount);

// js/custom-element/section/cart/cart-note.js
var CartNote = class extends HTMLTextAreaElement {
  connectedCallback() {
    this.addEventListener('change', this._onNoteChanged.bind(this));
  }
  get ownedToggle() {
    return this.hasAttribute('aria-owns') ? document.getElementById(this.getAttribute('aria-owns')) : null;
  }
  async _onNoteChanged() {
    if (this.ownedToggle) {
      this.ownedToggle.innerHTML = this.value === '' ? window.themeVariables.strings.cartAddOrderNote : window.themeVariables.strings.cartEditOrderNote;
    }
    const response = await fetch(`${window.themeVariables.routes.cartUrl}/update.js`, {
      body: JSON.stringify({ note: this.value }),
      credentials: 'same-origin',
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    const cartContent = await response.json();
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:updated', {
        bubbles: true,
        detail: {
          cart: cartContent,
        },
      })
    );
  }
};
window.customElements.define('cart-note', CartNote, { extends: 'textarea' });

// js/custom-element/section/cart/free-shipping-bar.js
var FreeShippingBar = class extends HTMLElement {
  connectedCallback() {
    document.documentElement.addEventListener('cart:updated', this._onCartUpdated.bind(this));
  }
  get threshold() {
    return parseFloat(this.getAttribute('threshold'));
  }
  _onCartUpdated(event) {
    const totalPrice = event.detail['cart']['items'].filter((item) => item['requires_shipping']).reduce((sum, item) => sum + item['final_line_price'], 0);
    this.style.setProperty('--progress', Math.min(totalPrice / this.threshold, 1));
  }
};
window.customElements.define('free-shipping-bar', FreeShippingBar);

// js/custom-element/section/product/review-link.js
var ReviewLink = class extends HTMLAnchorElement {
  constructor() {
    super();
    this.addEventListener('click', this._onClick.bind(this));
  }
  _onClick() {
    const shopifyReviewsElement = document.getElementById('shopify-product-reviews');
    if (!shopifyReviewsElement) {
      return;
    }
    if (window.matchMedia(window.themeVariables.breakpoints.pocket).matches) {
      shopifyReviewsElement.closest('collapsible-content').open = true;
    } else {
      document.querySelector(`[aria-controls="${shopifyReviewsElement.closest('.product-tabs__tab-item-wrapper').id}"]`).click();
    }
  }
};
window.customElements.define('review-link', ReviewLink, { extends: 'a' });

// js/theme.js
(() => {
  new InputBindingManager();
})();
(() => {
  if (Shopify.designMode) {
    document.addEventListener('shopify:section:load', () => {
      if (window.SPR) {
        window.SPR.initDomEls();
        window.SPR.loadProducts();
      }
    });
  }
  window.SPRCallbacks = {
    onFormSuccess: (event, info) => {
      document.getElementById(`form_${info.id}`).classList.add('spr-form--success');
    },
  };
})();

// START WIP
document.addEventListener('DOMContentLoaded', () => {
  // Fix for mobile browsers (Instagram, Safari) 100vh issue
  // Record the initial viewport height and store it as a CSS custom property
  (() => {
    const vh = window.innerHeight;
    document.documentElement.style.setProperty('--vh-initial', `${vh}px`);

    // Optional: Update on resize but only if the width changes (to avoid keyboard-triggered resizes)
    let windowWidth = window.innerWidth;
    window.addEventListener('resize', () => {
      if (window.innerWidth !== windowWidth) {
        windowWidth = window.innerWidth;
        const newVh = window.innerHeight;
        document.documentElement.style.setProperty('--vh-initial', `${newVh}px`);
      }
    });
  })();

  (() => {
    const rootElement = document.documentElement;
    const storageKey = 'sticky-element-position';
    let stickyPosition = null;

    // Cache computed styles to avoid repetitive calls
    let stickyHeaderOffset = null;

    const getStickyHeaderOffset = () => {
      if (stickyHeaderOffset === null) {
        const documentStyles = getComputedStyle(rootElement);
        stickyHeaderOffset = parseInt(documentStyles.getPropertyValue('--header-height') || 0) * parseInt(documentStyles.getPropertyValue('--enable-sticky-header') || 0) + parseInt(documentStyles.getPropertyValue('--announcement-bar-height') || 0) * parseInt(documentStyles.getPropertyValue('--enable-sticky-announcement-bar') || 0);
      }
      return stickyHeaderOffset;
    };

    const getElementHeightByClass = (className) => {
      const element = document.querySelector(`.${className}`);
      return element ? element.offsetHeight : 0;
    };

    const setStickyTopOffset = () => {
      const stickyHeaderOffset = getStickyHeaderOffset();

      document.querySelectorAll('[data-apply-top-class]').forEach((el) => {
        let offset = stickyHeaderOffset;

        if (el.getAttribute('data-offset') === 'true') {
          if (el.hasAttribute('data-scroll-offset')) {
            offset += parseInt(el.getAttribute('data-scroll-offset') || 0);
          }
          if (el.hasAttribute('data-scroll-offset-class')) {
            const offsetClass = el.getAttribute('data-scroll-offset-class');
            offset += getElementHeightByClass(offsetClass);
          }
        }

        // Apply to the element itself if the data-apply-top-class points to its own class
        const applyTopClass = el.getAttribute('data-apply-top-class');
        if (el.classList.contains(applyTopClass)) {
          el.style.setProperty('--sticky-top', `${offset}px`);
        } else {
          document.querySelectorAll(`.${applyTopClass}`).forEach((target) => {
            target.style.setProperty('--sticky-top', `${offset}px`);
          });
        }
      });
    };

    const applyStickyClassImmediately = () => {
      const storedPosition = sessionStorage.getItem(storageKey);

      if (storedPosition !== null) {
        stickyPosition = parseInt(storedPosition, 10);
        document.querySelectorAll('[data-sticky]').forEach((el) => {
          if (window.scrollY >= stickyPosition) {
            el.classList.add('is-stuck');
          }
        });
      }
    };

    const handleScroll = () => {
      const stickyHeaderOffset = getStickyHeaderOffset();
      let elementsScrolledPast = 0;

      const scrollPastElements = document.querySelectorAll('[data-scroll-past-alert]');
      scrollPastElements.forEach((el, index) => {
        const rect = el.getBoundingClientRect();
        let offset = 0;

        if (el.getAttribute('data-offset') === 'true') {
          if (el.hasAttribute('data-scroll-offset')) {
            offset += parseInt(el.getAttribute('data-scroll-offset') || 0);
          }
          if (el.hasAttribute('data-scroll-offset-class')) {
            const offsetClass = el.getAttribute('data-scroll-offset-class');
            offset += getElementHeightByClass(offsetClass);
          }
        }

        if (rect.top < stickyHeaderOffset + offset + 1) {
          elementsScrolledPast = index + 1;
        }
      });

      // Update classes in a single reflow
      requestAnimationFrame(() => {
        rootElement.className = rootElement.className.replace(/(\s|^)page-scrolling--past-\d+(\s|$)/g, ' ');

        if (elementsScrolledPast > 0) {
          rootElement.classList.add(`page-scrolling--past-${elementsScrolledPast}`);
        }

        if (window.scrollY > 0) {
          rootElement.classList.add('page-scrolling');
        } else {
          rootElement.classList.remove('page-scrolling');
        }
      });

      document.querySelectorAll('[data-sticky]').forEach((el) => {
        const rect = el.getBoundingClientRect();
        const stickyTop = parseInt(getComputedStyle(el).top) || 0;
        const shouldBeStuck = rect.top <= stickyTop && window.scrollY > 0;

        if (shouldBeStuck && stickyPosition === null) {
          // Record the exact scroll position when the element becomes sticky
          stickyPosition = window.scrollY;
          sessionStorage.setItem(storageKey, stickyPosition);
          el.classList.add('is-stuck');
        } else if (stickyPosition !== null) {
          if (window.scrollY >= stickyPosition) {
            // If we're at or below the sticky position, ensure the class is applied
            el.classList.add('is-stuck');
          } else if (window.scrollY < stickyPosition) {
            // Remove the sticky class only if we scroll above the recorded sticky position
            el.classList.remove('is-stuck');
          }
        }
      });
    };

    const handleInitialLoad = () => {
      setStickyTopOffset();
      applyStickyClassImmediately(); // Apply sticky state on page load before render
    };

    let scrollTimeout;
    const onScroll = () => {
      if (!scrollTimeout) {
        scrollTimeout = requestAnimationFrame(() => {
          scrollTimeout = null;
          handleScroll(); // Handle scroll events
        });
      }
    };

    let resizeTimeout;
    const onResize = () => {
      if (!resizeTimeout) {
        resizeTimeout = requestAnimationFrame(() => {
          resizeTimeout = null;
          stickyHeaderOffset = null; // Reset the cached offset on resize
          setStickyTopOffset();
          handleScroll(); // Handle resize events
        });
      }
    };

    // Add event listeners for scroll and resize
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize, { passive: true });

    // Use MutationObserver to listen for changes in the DOM
    const observer = new MutationObserver(() => {
      stickyHeaderOffset = null; // Reset the cached offset on mutation
      setStickyTopOffset();
      handleScroll(); // Handle DOM mutations
    });

    observer.observe(document.body, { childList: true, subtree: true });

    // Cleanup observer on unload
    window.addEventListener('unload', () => {
      observer.disconnect();
    });

    // Initial sticky detection and immediate CSS application
    handleInitialLoad(); // Initial check on load
  })();
});

// document.addEventListener('DOMContentLoaded', () => {
//   (() => {
//     let previousClientWidth = window.visualViewport ? window.visualViewport.width : document.documentElement.clientWidth;
//     const rootElement = document.documentElement;

//     const setViewportProperty = () => {
//       const clientWidth = window.visualViewport ? window.visualViewport.width : document.documentElement.clientWidth;
//       const clientHeight = window.visualViewport ? window.visualViewport.height : document.documentElement.clientHeight;
//       if (clientWidth === previousClientWidth) return;

//       requestAnimationFrame(() => {
//         rootElement.style.setProperty('--window-height', `${clientHeight}px`);
//         rootElement.style.setProperty('--window-width', `${clientWidth}px`);
//         previousClientWidth = clientWidth;
//       });
//     };

//     const getStickyHeaderOffset = () => {
//       const documentStyles = getComputedStyle(rootElement);
//       return parseInt(documentStyles.getPropertyValue('--header-height') || 0) * parseInt(documentStyles.getPropertyValue('--enable-sticky-header') || 0) + parseInt(documentStyles.getPropertyValue('--announcement-bar-height') || 0) * parseInt(documentStyles.getPropertyValue('--enable-sticky-announcement-bar') || 0);
//     };

//     const getElementHeightByClass = (className) => {
//       const element = document.querySelector(`.${className}`);
//       return element ? element.offsetHeight : 0;
//     };

//     const setStickyTopOffset = () => {
//       const stickyHeaderOffset = getStickyHeaderOffset();

//       document.querySelectorAll('[data-apply-top-class]').forEach((el) => {
//         let offset = stickyHeaderOffset;

//         if (el.getAttribute('data-offset') === 'true') {
//           if (el.hasAttribute('data-scroll-offset')) {
//             offset += parseInt(el.getAttribute('data-scroll-offset') || 0);
//           }
//           if (el.hasAttribute('data-scroll-offset-class')) {
//             const offsetClass = el.getAttribute('data-scroll-offset-class');
//             offset += getElementHeightByClass(offsetClass);
//           }
//         }

//         const applyTopClass = el.getAttribute('data-apply-top-class');
//         document.querySelectorAll(`.${applyTopClass}`).forEach((target) => {
//           target.style.setProperty('--sticky-top', `${offset}px`);
//         });
//       });
//     };

//     const handleScroll = () => {
//       const stickyHeaderOffset = getStickyHeaderOffset();
//       let elementsScrolledPast = 0;

//       document.querySelectorAll('[data-scroll-past-alert]').forEach((el, index) => {
//         const rect = el.getBoundingClientRect();
//         let offset = 0;

//         if (el.getAttribute('data-offset') === 'true') {
//           if (el.hasAttribute('data-scroll-offset')) {
//             offset += parseInt(el.getAttribute('data-scroll-offset') || 0);
//           }
//           if (el.hasAttribute('data-scroll-offset-class')) {
//             const offsetClass = el.getAttribute('data-scroll-offset-class');
//             offset += getElementHeightByClass(offsetClass);
//           }
//         }

//         if (rect.top < stickyHeaderOffset + offset + 1) {
//           elementsScrolledPast = index + 1;
//         }
//       });

//       // Update classes in a single reflow
//       requestAnimationFrame(() => {
//         rootElement.className = rootElement.className.replace(/(\s|^)page-scrolling--past-\d+(\s|$)/g, ' ');

//         if (elementsScrolledPast > 0) {
//           rootElement.classList.add(`page-scrolling--past-${elementsScrolledPast}`);
//         }

//         if (window.scrollY > 0) {
//           rootElement.classList.add('page-scrolling');
//         } else {
//           rootElement.classList.remove('page-scrolling');
//         }
//       });
//     };

//     // Initial call to set the sticky top offset
//     setStickyTopOffset();

//     let scrollTimeout;
//     const onScroll = () => {
//       if (!scrollTimeout) {
//         scrollTimeout = setTimeout(() => {
//           scrollTimeout = null;
//           handleScroll();
//         }, 16); // 16ms for 60fps
//       }
//     };

//     let resizeTimeout;
//     const onResize = () => {
//       if (!resizeTimeout) {
//         resizeTimeout = setTimeout(() => {
//           resizeTimeout = null;
//           setViewportProperty();
//           setStickyTopOffset();
//         }, 16); // 16ms for 60fps
//       }
//     };

//     // Add event listeners for scroll and resize
//     window.addEventListener('scroll', onScroll, { passive: true });
//     window.addEventListener('resize', onResize, { passive: true });

//     // Use MutationObserver to listen for changes in the DOM
//     const observer = new MutationObserver(() => {
//       setStickyTopOffset();
//       handleScroll();
//     });

//     observer.observe(document.body, { childList: true, subtree: true });

//     // Cleanup observer on unload
//     window.addEventListener('unload', () => {
//       observer.disconnect();
//     });
//   })();
// });

// (() => {
//   let previousClientWidth = window.visualViewport ? window.visualViewport.width : document.documentElement.clientWidth;

//   let setViewportProperty = () => {
//     const clientWidth = window.visualViewport ? window.visualViewport.width : document.documentElement.clientWidth,
//       clientHeight = window.visualViewport ? window.visualViewport.height : document.documentElement.clientHeight;
//     if (clientWidth === previousClientWidth) {
//       return;
//     }
//     requestAnimationFrame(() => {
//       document.documentElement.style.setProperty('--window-height', clientHeight + 'px');
//       document.documentElement.style.setProperty('--window-width', clientWidth + 'px');
//       previousClientWidth = clientWidth;
//     });
//   };

//   setViewportProperty();

//   if (window.visualViewport) {
//     window.visualViewport.addEventListener('resize', setViewportProperty);
//   } else {
//     window.addEventListener('resize', setViewportProperty);
//   }

//   // Function to get the height of an element by class name
//   const getElementHeightByClass = (className) => {
//     const element = document.querySelector(`.${className}`);
//     return element ? element.offsetHeight : 0;
//   };

//   // Function to calculate and set the sticky top offset
//   const setStickyTopOffset = () => {
//     const stickyHeaderOffset = getStickyHeaderOffset();

//     document.querySelectorAll('[data-apply-top-class]').forEach((el) => {
//       let offset = stickyHeaderOffset;

//       if (el.getAttribute('data-offset') === 'true') {
//         if (el.hasAttribute('data-scroll-offset')) {
//           offset += parseInt(el.getAttribute('data-scroll-offset') || 0);
//         }
//         if (el.hasAttribute('data-scroll-offset-class')) {
//           const offsetClass = el.getAttribute('data-scroll-offset-class');
//           offset += getElementHeightByClass(offsetClass);
//         }
//       }

//       const applyTopClass = el.getAttribute('data-apply-top-class');
//       const targetElements = document.querySelectorAll(`.${applyTopClass}`);
//       targetElements.forEach((target) => {
//         target.style.setProperty('--sticky-top', `${offset}px`);
//       });
//     });
//   };

//   // Initial call to set the sticky top offset
//   setStickyTopOffset();

//   // Add event listener for scroll
//   window.addEventListener('scroll', () => {
//     if (window.scrollY > 0) {
//       document.documentElement.classList.add('page-scrolling');
//     } else {
//       document.documentElement.classList.remove('page-scrolling');
//     }

//     const stickyHeaderOffset = getStickyHeaderOffset();

//     // Find all elements with the data-scroll-past-alert attribute
//     const scrollPastElements = document.querySelectorAll('[data-scroll-past-alert]');
//     let elementsScrolledPast = 0;

//     scrollPastElements.forEach((el, index) => {
//       const rect = el.getBoundingClientRect();
//       let offset = 0;

//       if (el.getAttribute('data-offset') === 'true') {
//         if (el.hasAttribute('data-scroll-offset')) {
//           offset += parseInt(el.getAttribute('data-scroll-offset') || 0);
//         }
//         if (el.hasAttribute('data-scroll-offset-class')) {
//           const offsetClass = el.getAttribute('data-scroll-offset-class');
//           offset += getElementHeightByClass(offsetClass);
//         }
//       }

//       if (rect.top < stickyHeaderOffset + offset + 1) {
//         // Trigger 1 pixel past the top of the element
//         elementsScrolledPast = index + 1;
//       }
//     });

//     // Remove any existing .page-scrolling--past-* classes
//     document.documentElement.className = document.documentElement.className.replace(/(\s|^)page-scrolling--past-\d+(\s|$)/g, ' ');

//     // Add the new .page-scrolling--past-* class
//     if (elementsScrolledPast > 0) {
//       document.documentElement.classList.add(`page-scrolling--past-${elementsScrolledPast}`);
//     }
//   });

//   // Recalculate the sticky top offset on window resize
//   window.addEventListener('resize', setStickyTopOffset);
// })();

// END WIP

(() => {
  let documentDelegate = new main_default(document.body);
  documentDelegate.on('change', 'input:not([type="checkbox"]):not([type="radio"]), textarea', (event, target) => {
    target.classList.toggle('is-filled', target.value !== '');
  });
  documentDelegate.on('change', 'select', (event, target) => {
    target.parentNode.classList.toggle('is-filled', target.value !== '');
  });
})();
(() => {
  document.querySelectorAll('.rte table').forEach((table) => {
    table.outerHTML = '<div class="table-wrapper">' + table.outerHTML + '</div>';
  });
  document.querySelectorAll('.rte iframe').forEach((iframe) => {
    if (iframe.src.indexOf('youtube') !== -1 || iframe.src.indexOf('youtu.be') !== -1 || iframe.src.indexOf('vimeo') !== -1) {
      iframe.outerHTML = '<div class="video-wrapper">' + iframe.outerHTML + '</div>';
    }
  });
})();
(() => {
  let documentDelegate = new main_default(document.documentElement);
  documentDelegate.on('click', '[data-smooth-scroll]', (event, target) => {
    const elementToScroll = document.querySelector(target.getAttribute('href'));
    if (elementToScroll) {
      event.preventDefault();
      elementToScroll.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  });
})();
(() => {
  document.addEventListener('keyup', function (event) {
    if (event.key === 'Tab') {
      document.body.classList.remove('no-focus-outline');
      document.body.classList.add('focus-outline');
    }
  });
})();
/*! Bundled license information:

tabbable/dist/index.esm.js:
  (*!
  * tabbable 6.2.0
  * @license MIT, https://github.com/focus-trap/tabbable/blob/master/LICENSE
  *)

focus-trap/dist/focus-trap.esm.js:
  (*!
  * focus-trap 7.5.4
  * @license MIT, https://github.com/focus-trap/focus-trap/blob/master/LICENSE
  *)
*/


class SafeSticky extends HTMLElement {
  constructor() {
    super();
    this.resizeObserver = new ResizeObserver(this.recalculateStyles.bind(this));
    this.checkPositionListener = this.throttledCheckPosition.bind(this);
    this.initialTop = 0;
    this.lastKnownY = 0;
    this.currentTop = 0;
    this.position = "relative";
    this.requestId = null;
    this.lastArgs = [];
    this.cleanupCallback = null;
  }

  connectedCallback() {
    this.inView(
      () => {
        window.addEventListener("scroll", this.checkPositionListener);
        this.resizeObserver.observe(this);

        // Store the cleanup function for later when the element goes out of view
        this.cleanupCallback = () => {
          window.removeEventListener("scroll", this.checkPositionListener);
          this.resizeObserver.unobserve(this);
        };
      },
      { margin: "500px" }
    );
  }

  disconnectedCallback() {
    // Ensure cleanup if the component is removed from the DOM
    if (this.cleanupCallback) this.cleanupCallback();
  }

  recalculateStyles() {
    this.style.removeProperty("top");
    const computedStyles = getComputedStyle(this);
    this.initialTop = parseInt(computedStyles.top, 10);
    this.position = computedStyles.position;
    this.checkPosition();
  }

  checkPosition() {
    if (this.position !== "sticky") {
      return this.style.removeProperty("top");
    }

    const bounds = this.getBoundingClientRect();
    const maxTop =
      bounds.top + window.scrollY - this.offsetTop + this.initialTop;
    const minTop = this.clientHeight - window.innerHeight + 20;

    if (window.scrollY < this.lastKnownY) {
      this.currentTop -= window.scrollY - this.lastKnownY;
    } else {
      this.currentTop += this.lastKnownY - window.scrollY;
    }

    this.currentTop = Math.min(
      Math.max(this.currentTop, -minTop),
      maxTop,
      this.initialTop
    );

    this.lastKnownY = window.scrollY;
    this.style.top = `${Math.round(this.currentTop)}px`;
  }

  throttledCheckPosition(...args) {
    this.lastArgs = args;
    if (this.requestId === null) {
      this.requestId = requestAnimationFrame(() => {
        this.requestId = null;
        this.checkPosition(...this.lastArgs);
      });
    }
  }

  inView(callback, options) {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        if (!this.cleanupCallback) {
          // Only set up the intersection once when entering the view
          callback();
        }
      } else {
        // When the element is no longer in view, call cleanup
        if (this.cleanupCallback) {
          this.cleanupCallback();
          this.cleanupCallback = null; // Reset to prevent duplicate calls
        }
      }
    }, options);
    observer.observe(this);
  }
}

// Register the custom element
customElements.define("safe-sticky", SafeSticky);